import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Tabs,
  Tab,
  Grid,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Autocomplete,
  Divider,
  IconButton,
  InputAdornment,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import carsData from "../cars.json";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { createBooking, getBrands } from "../services/api/getAll";
import LocalCarWashIcon from "@mui/icons-material/LocalCarWash";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MapIcon from "@mui/icons-material/Map";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import RateReviewIcon from "@mui/icons-material/RateReview";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import BuildIcon from "@mui/icons-material/Build";
import LocalGasStationIcon from "@mui/icons-material/LocalGasStation";
import PaletteIcon from "@mui/icons-material/Palette";

// Import AI-generated premium car wash images
import washFullWash from "../assets/wash_full_wash.png";
import washInteriorWash from "../assets/wash_interior_wash.png";
import washExteriorWash from "../assets/wash_exterior_wash.png";
import washMiniCare from "../assets/wash_mini_care.png";
import washSuperCare from "../assets/wash_super_care.png";
import washOverhaul from "../assets/wash_overhaul.png";
import washPolishWax from "../assets/wash_polish_wax.png";
import washCeramicPrep from "../assets/wash_ceramic_prep.png";
import washScratchRemoval from "../assets/wash_scratch_removal.png";
import washHeadlight from "../assets/wash_headlight.png";

// Predefined services to match the database items
const servicesList = [
  { id: 7, key: "wash", titleAr: "خدمات الغسيل والتنظيف", titleEn: "Washing & Cleaning Services", icon: LocalCarWashIcon },
  { id: 1, key: "towing", titleAr: "خدمة الونش", titleEn: "Towing Service", icon: DirectionsCarIcon },
  { id: 2, key: "battery", titleAr: "تشغيل البطارية", titleEn: "Battery Jumpstart", icon: FlashOnIcon },
  { id: 3, key: "tire", titleAr: "تغيير الإطارات", titleEn: "Flat Tire Change", icon: BuildIcon },
  { id: 4, key: "fuel", titleAr: "توصيل الوقود", titleEn: "Fuel Delivery", icon: LocalGasStationIcon },
];

// Car wash packages data from the user's provided menu image
const carWashPackages = {
  wash: [
    {
      id: "exterior_wash",
      nameAr: "غسيل خارجي",
      nameEn: "Exterior Wash",
      price: 200,
      image: washExteriorWash,
      detailsAr: [
        "غسيل بالفوم",
        "تنشيف كامل",
        "تلميع الإطارات"
      ],
      detailsEn: [
        "Foam wash",
        "Complete drying",
        "Tire polishing"
      ],
    },
    {
      id: "interior_wash",
      nameAr: "غسيل داخلي",
      nameEn: "Interior Wash",
      price: 200,
      image: washInteriorWash,
      detailsAr: [
        "شفط الأتربة",
        "تنظيف وتلميع التابلوه",
        "غسيل الدواسات"
      ],
      detailsEn: [
        "Dust vacuuming",
        "Dashboard cleaning & polishing",
        "Car mat washing"
      ],
    },
    {
      id: "full_wash",
      nameAr: "الغسيل الكامل (داخلي وخارجي)",
      nameEn: "Full Wash (Interior & Exterior)",
      price: 350,
      image: washFullWash,
      detailsAr: [
        "غسيل خارجي",
        "تلميع الإطارات",
        "شفط الأتربة",
        "غسيل الدواسات",
        "تنظيف وتلميع التابلوه",
        "تنظيف القوائم",
        "تنظيف الشنطة (حسب الرغبة)"
      ],
      detailsEn: [
        "Exterior wash",
        "Tire polishing",
        "Dust vacuuming",
        "Car mat washing",
        "Dashboard cleaning & polishing",
        "Door frame cleaning",
        "Trunk cleaning (optional)"
      ],
    },
  ],
  deep: [
    {
      id: "mini_care",
      nameAr: "ميني كار كير",
      nameEn: "Mini Car Care",
      price: 650,
      image: washMiniCare,
      detailsAr: ["تطهير وتعقيم للسيارة من داخل"],
      detailsEn: ["Interior disinfection and sanitization"],
    },
    {
      id: "super_care",
      nameAr: "سوبر كار كير",
      nameEn: "Super Car Care",
      price: 950,
      image: washSuperCare,
      detailsAr: ["إزالة البقع والأوساخ من: الكراسي - القوائم - فتحات التكييف - الأبواب - الشنطة"],
      detailsEn: ["Stain and dirt removal from: Seats - door frames - AC vents - doors - trunk"],
    },
    {
      id: "overhaul",
      nameAr: "توهيلب كار كير",
      nameEn: "TowHelp Car Care",
      price: 1500,
      image: washOverhaul,
      detailsAr: [
        "تنظيف عميق وإزالة البقع من: الكراسي - السقف - القوائم - فتحات التكييف - الأبواب - الشنطة - دواسات البنزين - حاملات الأكواب",
        "تنظيف 4 جنوط",
        "تنظيف المحرك (اختياري)"
      ],
      detailsEn: [
        "Deep cleaning & stain removal: Seats - ceiling - door frames - AC vents - doors - trunk - pedals - cup holders",
        "4 rims cleaning",
        "Engine cleaning (optional)"
      ],
    },
  ],
  polish: [
    {
      id: "polish_wax",
      nameAr: "بوليش + واكس",
      nameEn: "Polish + Wax",
      price: 1200,
      image: washPolishWax,
      detailsAr: ["إعادة اللمعان وحماية الدهان بطبقة واكس عالية الجودة"],
      detailsEn: ["Restore shine and protect paint with a high-quality wax layer"],
    },
    {
      id: "ceramic_prep",
      nameAr: "تجهيز طبقات الحماية",
      nameEn: "Ceramic Coating Prep",
      price: 1800,
      image: washCeramicPrep,
      detailsAr: ["تلميع احترافي على مرحلتين + تجهيز السيارة لطبقات الحماية"],
      detailsEn: ["Professional 2-stage polishing + preparing car for protection layers"],
    },
    {
      id: "scratch_removal",
      nameAr: "معالجة الخدوش وآثار الغسيل",
      nameEn: "Scratch & Swirl Removal",
      price: 2500,
      image: washScratchRemoval,
      detailsAr: ["من 3 إلى 4 مراحل تلميع كمباوند + بوليش + واكس"],
      detailsEn: ["3 to 4 stages of compound polishing + polish + wax"],
    },
    {
      id: "headlight",
      nameAr: "تلميع الفوانيس الأمامية",
      nameEn: "Headlight Restoration",
      price: 600,
      image: washHeadlight,
      detailsAr: ["إزالة العتامة وإعادة الشفافية واللمعان للفوانيس"],
      detailsEn: ["Remove oxidation and restore clarity and shine to headlights"],
    },
  ],
};

const carColors = [
  { key: "white", nameAr: "أبيض", nameEn: "White" },
  { key: "black", nameAr: "أسود", nameEn: "Black" },
  { key: "silver", nameAr: "فضي", nameEn: "Silver" },
  { key: "grey", nameAr: "رمادي", nameEn: "Grey" },
  { key: "red", nameAr: "أحمر", nameEn: "Red" },
  { key: "blue", nameAr: "أزرق", nameEn: "Blue" },
  { key: "gold", nameAr: "ذهبي", nameEn: "Gold" },
  { key: "bronze", nameAr: "برونزي", nameEn: "Bronze" },
  { key: "brown", nameAr: "بني", nameEn: "Brown" },
  { key: "green", nameAr: "أخضر", nameEn: "Green" },
  { key: "yellow", nameAr: "أصفر", nameEn: "Yellow" }
];

const BookingForm = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Form values
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [carModel, setCarModel] = useState("");
  const [dateTime, setDateTime] = useState("");
  const [location, setLocation] = useState("");
  const [notes, setNotes] = useState("");

  // Custom Picker States
  const [customPickerOpen, setCustomPickerOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [pickerDate, setPickerDate] = useState<Date>(new Date());
  
  const getTodayStr = () => {
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, '0');
    const d = String(today.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const [selectedDateStr, setSelectedDateStr] = useState<string>(getTodayStr());
  const [selectedTimeStr, setSelectedTimeStr] = useState<string>("10:00");

  // Custom Alert States
  const [customAlert, setCustomAlert] = useState<{ open: boolean; title: string; message: string } | null>(null);

  const showCustomAlert = (message: string, title = "") => {
    setCustomAlert({
      open: true,
      title: title || (isArabic ? "تنبيه" : "Alert"),
      message,
    });
  };

  const getDaysArray = () => {
    const year = pickerDate.getFullYear();
    const month = pickerDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayIndex = new Date(year, month, 1).getDay();
    
    const days: { dayNum: number | null; dateStr: string; disabled: boolean }[] = [];
    
    // Empty offsets
    for (let i = 0; i < firstDayIndex; i++) {
      days.push({ dayNum: null, dateStr: "", disabled: true });
    }
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (let d = 1; d <= daysInMonth; d++) {
      const currentDayDate = new Date(year, month, d);
      currentDayDate.setHours(0, 0, 0, 0);
      
      const formattedDateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      
      days.push({
        dayNum: d,
        dateStr: formattedDateStr,
        disabled: currentDayDate < today,
      });
    }
    
    return days;
  };

  const monthsAr = [
    "يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو",
    "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"
  ];
  const monthsEn = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const washSlots = [
    "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", 
    "17:00", "18:00", "19:00", "20:00", "21:00", "22:00", "23:00"
  ];

  const towingSlots = [
    "00:00", "01:00", "02:00", "03:00", "04:00", "05:00", "06:00", "07:00", "08:00", "09:00", "10:00", "11:00",
    "12:00", "13:00", "14:00", "15:00", "16:00", "17:00", "18:00", "19:00", "20:00", "21:00", "22:00", "23:00"
  ];

  const isTimeSlotDisabled = (timeStr: string) => {
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, '0');
    const d = String(today.getDate()).padStart(2, '0');
    const todayStr = `${y}-${m}-${d}`;
    
    if (selectedDateStr === todayStr) {
      const [slotHour, slotMin] = timeStr.split(":").map(Number);
      const currentHour = today.getHours();
      const currentMin = today.getMinutes();
      
      if (slotHour < currentHour) return true;
      if (slotHour === currentHour && slotMin <= currentMin) return true;
    }
    return false;
  };

  const handleConfirmCustomPicker = () => {
    if (isTimeSlotDisabled(selectedTimeStr)) {
      showCustomAlert(
        isArabic
          ? "الوقت المختار قد مضى بالفعل اليوم. يرجى اختيار وقت آخر."
          : "The selected time has already passed today. Please select another time."
      );
      return;
    }
    setDateTime(`${selectedDateStr}T${selectedTimeStr}`);
    setCustomPickerOpen(false);
  };

  const nextMonth = () => {
    setPickerDate(new Date(pickerDate.getFullYear(), pickerDate.getMonth() + 1, 1));
  };
  const prevMonth = () => {
    setPickerDate(new Date(pickerDate.getFullYear(), pickerDate.getMonth() - 1, 1));
  };

  const handleDaySelect = (dateStr: string) => {
    setSelectedDateStr(dateStr);
    
    const hasCarWashService = cars.some(car => {
      const eff = getEffectiveService(car, cars[0]);
      return eff.serviceId === 7;
    });
    
    const slots = hasCarWashService ? washSlots : towingSlots;
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, '0');
    const d = String(today.getDate()).padStart(2, '0');
    const todayStr = `${y}-${m}-${d}`;
    
    if (dateStr === todayStr) {
      const currentHour = today.getHours();
      const currentMin = today.getMinutes();
      const [selHour, selMin] = selectedTimeStr.split(":").map(Number);
      
      if (selHour < currentHour || (selHour === currentHour && selMin <= currentMin)) {
        const firstValid = slots.find(timeStr => {
          const [h, min] = timeStr.split(":").map(Number);
          return h > currentHour || (h === currentHour && min > currentMin);
        });
        if (firstValid) {
          setSelectedTimeStr(firstValid);
        }
      }
    }
  };

  // Dynamic pricing and promo code state
  const [dynamicPrices, setDynamicPrices] = useState<Record<string, number>>({});
  const [dynamicPromos, setDynamicPromos] = useState<any[]>([]);
  const [promoCodeInput, setPromoCodeInput] = useState("");
  const [appliedPromo, setAppliedPromo] = useState<any>(null);
  const [promoError, setPromoError] = useState("");
  const [promoSuccess, setPromoSuccess] = useState("");

  const getPackagePrice = React.useCallback((pkgId: string, defaultPrice: number) => {
    if (dynamicPrices && dynamicPrices[pkgId] !== undefined) {
      return Number(dynamicPrices[pkgId]);
    }
    return defaultPrice;
  }, [dynamicPrices]);

  const calculateDiscountAmount = React.useCallback((pkg: any) => {
    if (!pkg || !appliedPromo) return 0;
    const originalPrice = getPackagePrice(pkg.id, pkg.price);
    if (appliedPromo.type === "percent") {
      return Math.round((originalPrice * appliedPromo.discount) / 100);
    } else {
      return Math.min(appliedPromo.discount, originalPrice);
    }
  }, [appliedPromo, getPackagePrice]);

  const calculateCarPrice = React.useCallback((pkg: any) => {
    if (!pkg) return 0;
    const originalPrice = getPackagePrice(pkg.id, pkg.price);
    const discount = calculateDiscountAmount(pkg);
    return Math.max(0, originalPrice - discount);
  }, [calculateDiscountAmount, getPackagePrice]);

  const handleApplyPromoCode = () => {
    setPromoError("");
    setPromoSuccess("");
    if (!promoCodeInput.trim()) {
      setPromoError(isArabic ? "الرجاء إدخال رمز الكوبون" : "Please enter a coupon code");
      return;
    }
    const found = dynamicPromos.find((p) => p.code === promoCodeInput.trim().toUpperCase());
    if (!found) {
      setPromoError(isArabic ? "رمز الكوبون غير صالح" : "Invalid coupon code");
      setAppliedPromo(null);
      return;
    }
    setAppliedPromo(found);
    setPromoSuccess(
      isArabic
        ? `تم تطبيق الكوبون بنجاح! خصم ${found.discount}${found.type === "percent" ? "%" : " ج.م"}`
        : `Coupon applied successfully! Save ${found.discount}${found.type === "percent" ? "%" : " EGP"}`
    );
  };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        // Try local storage cache first
        const localPrices = localStorage.getItem("dynamic_prices");
        const localPromos = localStorage.getItem("dynamic_promos");
        if (localPrices) setDynamicPrices(JSON.parse(localPrices));
        if (localPromos) setDynamicPromos(JSON.parse(localPromos));

        // Fetch latest brand settings
        const data = await getBrands();
        if (data && data.length > 0) {
          const latest = data[data.length - 1];
          if (latest.description && latest.description.trim().startsWith("{")) {
            const parsed = JSON.parse(latest.description);
            if (parsed.prices) {
              setDynamicPrices(parsed.prices);
              localStorage.setItem("dynamic_prices", JSON.stringify(parsed.prices));
            }
            if (parsed.promos) {
              setDynamicPromos(parsed.promos);
              localStorage.setItem("dynamic_promos", JSON.stringify(parsed.promos));
            }
          }
        }
      } catch (err) {
        console.error("Failed to load dynamic pricing/promos in booking form:", err);
      }
    };
    fetchSettings();
  }, [isArabic]);

  interface CarItem {
    selectedBrand: any;
    selectedModel: any;
    brandInput: string;
    modelInput: string;
    color: string;
    plateLetters: string;
    plateNumbers: string;
    // Per-car service fields
    useGlobalService: boolean;
    serviceId: number;
    carWashTab: number;
    selectedPackage: any;
  }

  const createDefaultCar = (isFirst: boolean = false): CarItem => ({
    selectedBrand: null,
    selectedModel: null,
    brandInput: "",
    modelInput: "",
    color: "",
    plateLetters: "",
    plateNumbers: "",
    useGlobalService: !isFirst, // First car is always the "global" reference
    serviceId: 7,
    carWashTab: 0,
    selectedPackage: carWashPackages.wash[0],
  });

  // Cars array state
  const [cars, setCars] = useState<CarItem[]>([createDefaultCar(true)]);

  const handleAddCar = () => {
    setCars((prev) => [
      ...prev,
      createDefaultCar(false),
    ]);
  };

  const handleRemoveCar = (index: number) => {
    setCars((prev) => prev.filter((_, idx) => idx !== index));
  };

  const updateCar = (index: number, fields: Partial<CarItem>) => {
    setCars((prev) =>
      prev.map((car, idx) => (idx === index ? { ...car, ...fields } : car))
    );
  };

  // Helper: resolve the effective service/package for a car
  const getEffectiveService = React.useCallback((car: CarItem, firstCar: CarItem) => {
    if (car.useGlobalService) {
      return { serviceId: firstCar.serviceId, carWashTab: firstCar.carWashTab, selectedPackage: firstCar.selectedPackage };
    }
    return { serviceId: car.serviceId, carWashTab: car.carWashTab, selectedPackage: car.selectedPackage };
  }, []);

  // Calculate total price across all cars
  const calculateAllCarsTotal = React.useCallback(() => {
    if (cars.length === 0) return 0;
    const firstCar = cars[0];
    return cars.reduce((sum, car) => {
      const eff = getEffectiveService(car, firstCar);
      if (eff.serviceId === 7 && eff.selectedPackage) {
        return sum + calculateCarPrice(eff.selectedPackage);
      }
      return sum;
    }, 0);
  }, [cars, getEffectiveService, calculateCarPrice]);

  // Calculate total discount across all cars
  const calculateAllCarsDiscount = React.useCallback(() => {
    if (cars.length === 0) return 0;
    const firstCar = cars[0];
    return cars.reduce((sum, car) => {
      const eff = getEffectiveService(car, firstCar);
      if (eff.serviceId === 7 && eff.selectedPackage) {
        return sum + calculateDiscountAmount(eff.selectedPackage);
      }
      return sum;
    }, 0);
  }, [cars, getEffectiveService, calculateDiscountAmount]);

  // Calculate total original price (before discount)
  const calculateAllCarsOriginalTotal = React.useCallback(() => {
    if (cars.length === 0) return 0;
    const firstCar = cars[0];
    return cars.reduce((sum, car) => {
      const eff = getEffectiveService(car, firstCar);
      if (eff.serviceId === 7 && eff.selectedPackage) {
        return sum + getPackagePrice(eff.selectedPackage.id, eff.selectedPackage.price);
      }
      return sum;
    }, 0);
  }, [cars, getEffectiveService, getPackagePrice]);

  // Check if any car has a wash service selected
  const hasAnyWashService = React.useCallback(() => {
    const firstCar = cars[0];
    return cars.some((car) => {
      const eff = getEffectiveService(car, firstCar);
      return eff.serviceId === 7 && eff.selectedPackage;
    });
  }, [cars, getEffectiveService]);

  // Helper to format all cars into a single descriptive string
  const formatCarsString = React.useCallback((carsListForFormat: CarItem[]) => {
    return carsListForFormat.map((car, idx) => {
      const brandName = car.selectedBrand
        ? (isArabic ? car.selectedBrand.name_ar : car.selectedBrand.name_en)
        : car.brandInput;
      const modelName = car.selectedModel
        ? (isArabic ? car.selectedModel.name_ar : car.selectedModel.name_en)
        : car.modelInput;
      const colorObj = carColors.find((c) => c.key === car.color);
      const colorName = colorObj ? (isArabic ? colorObj.nameAr : colorObj.nameEn) : "";
      
      let plateInfo = "";
      if (car.plateLetters || car.plateNumbers) {
        const letters = car.plateLetters ? car.plateLetters.trim() : "";
        const numbers = car.plateNumbers ? car.plateNumbers.trim() : "";
        plateInfo = ` - لوحة: ${letters} ${numbers}`.trim();
      }
      
      const details = [brandName, modelName, colorName ? `(${colorName})` : ""].filter(Boolean).join(" ");
      return `${idx + 1}. ${details}${plateInfo}`.trim();
    }).join(" | ");
  }, [isArabic]);

  // Sync cars list to carModel string state
  useEffect(() => {
    setCarModel(formatCarsString(cars));
  }, [cars, formatCarsString]);

  // Derived brands list from cars.json
  const brandsObj = carsData.brands as Record<string, any>;
  const brandsList = Object.keys(brandsObj).map((key) => ({
    id: key,
    name_ar: brandsObj[key].name_ar,
    name_en: brandsObj[key].name_en,
    models: brandsObj[key].models,
  }));

  // UI state
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);

  const handleCloseSuccessDialog = () => {
    setSuccessDialogOpen(false);
    setFullName("");
    setPhone("");
    setCarModel("");
    setDateTime("");
    setLocation("");
    setNotes("");
    setAppliedPromo(null);
    setPromoCodeInput("");
    setPromoError("");
    setPromoSuccess("");
    setCars([createDefaultCar(true)]);
  };

  // Handle service change for a specific car
  const handleCarServiceChange = (carIdx: number, serviceId: number) => {
    setCars((prev) =>
      prev.map((car, idx) => {
        if (idx !== carIdx) return car;
        const defaultPkg = serviceId === 7 ? carWashPackages.wash[0] : null;
        return { ...car, serviceId, carWashTab: 0, selectedPackage: defaultPkg };
      })
    );
  };

  // Handle wash tab change for a specific car
  const handleCarWashTabChange = (carIdx: number, tab: number) => {
    setCars((prev) =>
      prev.map((car, idx) => {
        if (idx !== carIdx) return car;
        let pkg: any = null;
        if (tab === 0) pkg = carWashPackages.wash[0];
        if (tab === 1) pkg = carWashPackages.deep[0];
        if (tab === 2) pkg = carWashPackages.polish[0];
        return { ...car, carWashTab: tab, selectedPackage: pkg };
      })
    );
  };

  // Handle package selection for a specific car
  const handleCarPackageSelect = (carIdx: number, pkg: any) => {
    setCars((prev) =>
      prev.map((car, idx) => (idx === carIdx ? { ...car, selectedPackage: pkg } : car))
    );
  };

  // Toggle between global service and custom service for car #2+
  const handleToggleGlobalService = (carIdx: number) => {
    setCars((prev) =>
      prev.map((car, idx) => {
        if (idx !== carIdx) return car;
        if (car.useGlobalService) {
          // Switching to custom: copy first car's service as starting point
          const firstCar = prev[0];
          return { ...car, useGlobalService: false, serviceId: firstCar.serviceId, carWashTab: firstCar.carWashTab, selectedPackage: firstCar.selectedPackage };
        } else {
          return { ...car, useGlobalService: true };
        }
      })
    );
  };

  const getServiceObjById = (serviceId: number) => {
    return servicesList.find((s) => s.id === serviceId) || servicesList[4];
  };

  const getCarWashCategoriesForCar = (carWashTab: number) => {
    const list = carWashTab === 0 ? carWashPackages.wash : carWashTab === 1 ? carWashPackages.deep : carWashPackages.polish;
    return list.map((pkg: any) => ({
      ...pkg,
      price: getPackagePrice(pkg.id, pkg.price)
    }));
  };

  const formatWhatsAppMessage = () => {
    const firstCar = cars[0];

    // Format multiple cars list line-by-line with per-car service info
    const carsLineByLine = cars.map((car, idx) => {
      const brandName = car.selectedBrand
        ? (isArabic ? car.selectedBrand.name_ar : car.selectedBrand.name_en)
        : car.brandInput;
      const modelName = car.selectedModel
        ? (isArabic ? car.selectedModel.name_ar : car.selectedModel.name_en)
        : car.modelInput;
      const colorObj = carColors.find((c) => c.key === car.color);
      const colorName = colorObj ? (isArabic ? colorObj.nameAr : colorObj.nameEn) : "";
      
      const letters = car.plateLetters ? car.plateLetters.trim() : "";
      const numbers = car.plateNumbers ? car.plateNumbers.trim() : "";
      const plate = (letters || numbers) ? `${letters} ${numbers}`.trim() : "غير محدد";

      // Per-car service info
      const eff = getEffectiveService(car, firstCar);
      const svcObj = getServiceObjById(eff.serviceId);
      const svcName = isArabic ? svcObj.titleAr : svcObj.titleEn;
      let pkgLine = "";
      if (eff.serviceId === 7 && eff.selectedPackage) {
        const pkgName = isArabic ? eff.selectedPackage.nameAr : eff.selectedPackage.nameEn;
        const price = calculateCarPrice(eff.selectedPackage);
        pkgLine = ` | 📦 ${pkgName} (${price} ${t("booking.currency")})`;
      }

      return `   ${idx + 1}. ${brandName} ${modelName} - اللون: ${colorName || "غير محدد"} - اللوحة: ${plate}\n      🛠️ الخدمة: ${svcName}${pkgLine}`;
    }).join("\n");

    const totalPrice = calculateAllCarsTotal();
    const totalDiscount = calculateAllCarsDiscount();
    const discountText = totalDiscount > 0 ? `\n🎟️ *إجمالي الخصم:* -${totalDiscount} ${t("booking.currency")}` : "";
    const totalLine = hasAnyWashService() ? `\n💰 *الإجمالي:* ${totalPrice} ${t("booking.currency")}${discountText}` : "";

    const message = `*طلب حجز خدمة جديدة من الموقع* 🚗💨
--------------------------
👤 *الاسم:* ${fullName}
📞 *الهاتف:* ${phone}
📅 *الموعد المفضل:* ${dateTime.replace("T", " ")}
📍 *العنوان:* ${location}
🚘 *بيانات السيارات والخدمات:*
${carsLineByLine}${totalLine}
📝 *ملاحظات:* ${notes || "لا يوجد"}`;

    return encodeURIComponent(message);
  };

  const handleWhatsAppRedirect = () => {
    const message = formatWhatsAppMessage();
    const whatsappUrl = `https://wa.me/201096099980?text=${message}`;
    window.open(whatsappUrl, "_blank");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess(false);

    const hasValidCar = cars.some(car => (car.selectedBrand || car.brandInput) && (car.selectedModel || car.modelInput));
    if (!fullName || !phone || !hasValidCar || !dateTime || !location) {
      setError(isArabic ? "يرجى ملء جميع الحقول المطلوبة وتحديد ماركة وموديل السيارة." : "Please fill out all required fields and specify car make and model.");
      setLoading(false);
      return;
    }

    // 1. Phone number validation (Egyptian mobile number format: 11 digits, starts with 010, 011, 012, 015)
    const phoneRegex = /^01[0125]\d{8}$/;
    if (!phoneRegex.test(phone.trim())) {
      setError(
        isArabic
          ? "يرجى إدخال رقم هاتف محمول صحيح مكون من 11 رقماً ويبدأ بـ (010، 011، 012، 015)."
          : "Please enter a valid mobile number (11 digits starting with 010, 011, 012, or 015)."
      );
      setLoading(false);
      return;
    }

    // 2. Detailed address validation (at least 10 characters and 2 words)
    const addressTrimmed = location.trim();
    const wordCount = addressTrimmed.split(/\s+/).filter(Boolean).length;
    if (addressTrimmed.length < 10 || wordCount < 2) {
      setError(
        isArabic
          ? "يرجى كتابة العنوان بشكل تفصيلي وواضح للوصول إليك (اسم الشارع، المنطقة، أو رابط خرائط جوجل)."
          : "Please enter a detailed and clear address (street name, area, or Google Maps link)."
      );
      setLoading(false);
      return;
    }

    const hasCarWashService = cars.some(car => {
      const eff = getEffectiveService(car, cars[0]);
      return eff.serviceId === 7;
    });

    if (dateTime && hasCarWashService) {
      const timePart = dateTime.split("T")[1];
      if (timePart) {
        const [hours, minutes] = timePart.split(":").map(Number);
        // Washing/cleaning is from 10:00 AM (10) to 11:00 PM (23:00)
        if (hours < 10 || hours > 23 || (hours === 23 && minutes > 0)) {
          setError(
            isArabic
              ? "مواعيد خدمة غسيل وتنظيف السيارات متاحة فقط من الساعة 10:00 صباحاً وحتى 11:00 مساءً. يرجى اختيار موعد آخر."
              : "Car washing & cleaning service hours are only available between 10:00 AM and 11:00 PM. Please select another time."
          );
          setLoading(false);
          return;
        }
      }
    }

    const firstCar = cars[0];

    const carBrands = cars.map(car => {
      return car.selectedBrand
        ? (isArabic ? car.selectedBrand.name_ar : car.selectedBrand.name_en)
        : car.brandInput;
    }).filter(Boolean).join(" | ");

    const carModels = cars.map(car => {
      return car.selectedModel
        ? (isArabic ? car.selectedModel.name_ar : car.selectedModel.name_en)
        : car.modelInput;
    }).filter(Boolean).join(" | ");

    const carColorsList = cars.map(car => {
      const colorObj = carColors.find((c) => c.key === car.color);
      return colorObj ? (isArabic ? colorObj.nameAr : colorObj.nameEn) : "";
    }).filter(Boolean).join(" | ");

    const carPlateLettersList = cars.map(car => car.plateLetters || "").filter(Boolean).join(" | ");
    const carPlateNumbersList = cars.map(car => car.plateNumbers || "").filter(Boolean).join(" | ");

    // Build per-car service details
    const carsDetailsArray = cars.map((car, idx) => {
      const eff = getEffectiveService(car, firstCar);
      const svcObj = getServiceObjById(eff.serviceId);
      const brandName = car.selectedBrand ? car.selectedBrand.name_en : car.brandInput;
      const modelName = car.selectedModel ? car.selectedModel.name_en : car.modelInput;
      const colorObj = carColors.find((c) => c.key === car.color);
      return {
        carIndex: idx + 1,
        brand: brandName,
        model: modelName,
        color: colorObj ? colorObj.nameEn : "",
        plateLetters: car.plateLetters,
        plateNumbers: car.plateNumbers,
        serviceName: svcObj.titleEn,
        serviceId: eff.serviceId,
        packageName: eff.serviceId === 7 && eff.selectedPackage ? eff.selectedPackage.nameEn : null,
        packagePrice: eff.serviceId === 7 && eff.selectedPackage ? calculateCarPrice(eff.selectedPackage) : null,
      };
    });

    // Get primary service name from first car
    const firstServiceObj = getServiceObjById(firstCar.serviceId);
    const totalPrice = calculateAllCarsTotal();
    const totalDiscount = calculateAllCarsDiscount();

    // Summary service/package names
    const uniqueServices = Array.from(new Set(carsDetailsArray.map(c => c.serviceName)));
    const serviceSummary = uniqueServices.length === 1 ? uniqueServices[0] : uniqueServices.join(" | ");
    const packageNames = carsDetailsArray.map(c => c.packageName).filter(Boolean);
    const packageSummary = packageNames.length > 0 ? packageNames.join(" | ") : null;

    const bookingPayload = {
      serviceId: firstCar.serviceId,
      serviceName: serviceSummary,
      packageName: packageSummary,
      packagePrice: totalPrice || null,
      price: totalPrice || null,
      clientName: fullName,
      clientPhone: phone,
      carBrand: carBrands,
      carModel: carModels,
      carColor: carColorsList,
      carPlateLetters: carPlateLettersList,
      carPlateNumbers: carPlateNumbersList,
      carFullDetails: carModel,
      carsCount: cars.length,
      carsDetails: JSON.stringify(carsDetailsArray),
      totalPrice: totalPrice,
      dateTime: dateTime,
      location: location,
      notes: notes + (appliedPromo ? `\n🎟️ Coupon: ${appliedPromo.code} (-${totalDiscount} EGP)` : ""),
    };

    try {
      // Create an independent booking record for EACH car so every car gets its own line/row in the database & dashboard
      for (let idx = 0; idx < cars.length; idx++) {
        const car = cars[idx];
        const eff = getEffectiveService(car, firstCar);
        const svcObj = getServiceObjById(eff.serviceId);

        const brandName = car.selectedBrand
          ? (isArabic ? car.selectedBrand.name_ar : car.selectedBrand.name_en)
          : car.brandInput;
        const modelName = car.selectedModel
          ? (isArabic ? car.selectedModel.name_ar : car.selectedModel.name_en)
          : car.modelInput;
        const colorObj = carColors.find((c) => c.key === car.color);
        const colorName = colorObj ? (isArabic ? colorObj.nameAr : colorObj.nameEn) : "";

        const letters = car.plateLetters ? car.plateLetters.trim() : "";
        const numbers = car.plateNumbers ? car.plateNumbers.trim() : "";

        const carPrice = eff.serviceId === 7 && eff.selectedPackage ? calculateCarPrice(eff.selectedPackage) : null;
        const pkgName = eff.serviceId === 7 && eff.selectedPackage ? eff.selectedPackage.nameEn : null;

        let plateInfo = "";
        if (letters || numbers) {
          plateInfo = ` - ${isArabic ? "لوحة" : "Plate"}: ${letters} ${numbers}`.trim();
        }

        const carFullString = [brandName, modelName, colorName ? `(${colorName})` : ""].filter(Boolean).join(" ") + plateInfo;
        const carLabel = cars.length > 1 ? ` (سيارة ${idx + 1}/${cars.length})` : "";

        const singleCarPayload = {
          serviceId: eff.serviceId,
          serviceName: svcObj.titleEn,
          packageName: pkgName,
          packagePrice: carPrice,
          price: carPrice,
          clientName: fullName + carLabel,
          clientPhone: phone,
          carBrand: brandName,
          carModel: modelName,
          carColor: colorName,
          carPlateLetters: letters,
          carPlateNumbers: numbers,
          carFullDetails: carFullString,
          carsCount: 1,
          totalPrice: carPrice || 0,
          dateTime: dateTime,
          location: location,
          notes: notes + (appliedPromo ? `\n🎟️ Coupon: ${appliedPromo.code}` : ""),
        };

        await createBooking(singleCarPayload);
      }

      setSuccess(true);
      setSuccessDialogOpen(true);
    } catch (err) {
      console.error(err);
      setError(
        isArabic
          ? "حدثت مشكلة أثناء إرسال الحجز لقاعدة البيانات. يمكنك إتمام الحجز مباشرة عبر واتساب وسيقوم فريقنا بخدمتك فوراً!"
          : "There was a problem sending the booking to the database. You can complete the booking directly via WhatsApp!"
      );
    } finally {
      setLoading(false);
    }
  };
  // Kept for backward compat, uses first car's tab
  const getCarWashCategories = () => {
    const tab = cars[0]?.carWashTab ?? 0;
    const list = tab === 0 ? carWashPackages.wash : tab === 1 ? carWashPackages.deep : carWashPackages.polish;
    return list.map((pkg: any) => ({
      ...pkg,
      price: getPackagePrice(pkg.id, pkg.price)
    }));
  };

  const renderStepHeader = (num: string, titleAr: string, titleEn: string) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4, mt: 4 }}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          backgroundColor: "rgba(255, 174, 0, 0.1)",
          border: "2px solid #FFAE00",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#FFAE00",
          fontWeight: 800,
          fontSize: "1rem",
          fontFamily: "Poppins",
        }}
      >
        {num}
      </Box>
      <Typography
        variant="h6"
        fontWeight={700}
        sx={{
          color: "#fff",
          fontFamily: "Poppins, Ozen",
          fontSize: { xs: "1.1rem", md: "1.25rem" },
          textAlign: isArabic ? "right" : "left",
        }}
      >
        {isArabic ? titleAr : titleEn}
      </Typography>
      <Box
        sx={{
          flex: 1,
          height: "1px",
          background: "linear-gradient(90deg, rgba(255, 174, 0, 0.2) 0%, rgba(255, 255, 255, 0.05) 100%)",
          ml: isArabic ? 0 : 2,
          mr: isArabic ? 2 : 0,
        }}
      />
    </Box>
  );

  const renderTextField = ({
    required = false,
    fullWidth = true,
    labelAr,
    labelEn,
    placeholderAr,
    placeholderEn,
    value,
    onChange,
    icon: IconComponent = null,
    type = "text",
    multiline = false,
    rows = 1,
    helperTextAr = "",
    helperTextEn = "",
    readOnly = false,
    onClick = undefined,
    endAdornment = null,
  }: {
    required?: boolean;
    fullWidth?: boolean;
    labelAr: string;
    labelEn: string;
    placeholderAr?: string;
    placeholderEn?: string;
    value: string;
    onChange?: (val: string) => void;
    icon?: any;
    type?: string;
    multiline?: boolean;
    rows?: number;
    helperTextAr?: string;
    helperTextEn?: string;
    readOnly?: boolean;
    onClick?: () => void;
    endAdornment?: React.ReactNode;
  }) => {
    const labelText = isArabic ? labelAr : labelEn;
    const placeholderText = isArabic ? placeholderAr : placeholderEn;

    return (
      <Box sx={{ width: "100%", mb: 3 }} onClick={onClick}>
        <Typography
          variant="body2"
          sx={{
            color: "#aaa",
            fontWeight: 600,
            mb: 1.2,
            display: "flex",
            alignItems: "center",
            flexDirection: isArabic ? "row-reverse" : "row",
            justifyContent: isArabic ? "flex-end" : "flex-start",
            width: "100%",
            gap: 1,
            fontFamily: "Poppins, Ozen",
            fontSize: "0.9rem",
          }}
        >
          {IconComponent && (
            <IconComponent sx={{ color: "#FFAE00", fontSize: "1.2rem" }} />
          )}
          <span>{labelText}</span>
          {required && <span style={{ color: "#ff4d4d", marginLeft: 2, marginRight: 2 }}>*</span>}
        </Typography>
        <TextField
          required={required}
          fullWidth={fullWidth}
          dir={isArabic ? "rtl" : "ltr"}
          type={type}
          multiline={multiline}
          rows={rows}
          placeholder={placeholderText}
          value={value}
          onChange={onChange ? (e) => onChange(e.target.value) : undefined}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            readOnly: readOnly,
            endAdornment: endAdornment,
          }}
          sx={{
            cursor: readOnly ? "pointer" : "inherit",
            "& .MuiOutlinedInput-root": {
              color: "#fff",
              fontFamily: "Poppins, Ozen",
              borderRadius: "16px",
              backgroundColor: "rgba(255, 255, 255, 0.02)",
              backdropFilter: "blur(8px)",
              "& input": {
                cursor: readOnly ? "pointer" : "inherit",
                textAlign: isArabic ? "right" : "left",
                padding: "16px 14px",
              },
              "& textarea": {
                textAlign: isArabic ? "right" : "left",
              },
              "& fieldset": {
                borderColor: "rgba(255, 174, 0, 0.12)",
                borderRadius: "16px",
                transition: "all 0.3s ease",
              },
              "&:hover fieldset": {
                borderColor: "rgba(255, 174, 0, 0.35)",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#FFAE00",
                boxShadow: "0 0 15px rgba(255, 174, 0, 0.15)",
              },
              "& input::-webkit-calendar-picker-indicator": {
                filter: "invert(70%) sepia(90%) saturate(1000%) hue-rotate(10deg)",
                cursor: "pointer",
              },
            },
          }}
        />
        {(isArabic ? helperTextAr : helperTextEn) && (
          <Typography
            variant="caption"
            sx={{
              color: "#FFAE00",
              mt: 1.2,
              display: "block",
              textAlign: isArabic ? "right" : "left",
              fontFamily: "Poppins, Ozen",
              fontSize: "0.8rem",
            }}
          >
            {isArabic ? helperTextAr : helperTextEn}
          </Typography>
        )}
      </Box>
    );
  };

  const renderAutocomplete = ({
    required = false,
    labelAr,
    labelEn,
    placeholderAr,
    placeholderEn,
    options,
    getOptionLabel,
    value,
    onChange,
    inputValue,
    onInputChange,
    icon: IconComponent,
    disabled = false,
  }: {
    required?: boolean;
    labelAr: string;
    labelEn: string;
    placeholderAr?: string;
    placeholderEn?: string;
    options: any[];
    getOptionLabel: (option: any) => string;
    value: any;
    onChange: (event: any, newValue: any) => void;
    inputValue: string;
    onInputChange: (event: any, newInputValue: string) => void;
    icon: any;
    disabled?: boolean;
  }) => {
    const labelText = isArabic ? labelAr : labelEn;
    const placeholderText = isArabic ? placeholderAr : placeholderEn;

    return (
      <Box sx={{ width: "100%", mb: 3 }}>
        <Typography
          variant="body2"
          sx={{
            color: "#aaa",
            fontWeight: 600,
            mb: 1.2,
            display: "flex",
            alignItems: "center",
            flexDirection: isArabic ? "row-reverse" : "row",
            justifyContent: isArabic ? "flex-end" : "flex-start",
            width: "100%",
            gap: 1,
            fontFamily: "Poppins, Ozen",
            fontSize: "0.9rem",
          }}
        >
          {IconComponent && (
            <IconComponent sx={{ color: "#FFAE00", fontSize: "1.2rem" }} />
          )}
          <span>{labelText}</span>
          {required && <span style={{ color: "#ff4d4d", marginLeft: 2, marginRight: 2 }}>*</span>}
        </Typography>
        <Autocomplete
          freeSolo
          disabled={disabled}
          options={options}
          getOptionLabel={(option) => {
            if (typeof option === "string") return option;
            return getOptionLabel(option);
          }}
          value={value}
          onChange={onChange}
          inputValue={inputValue}
          onInputChange={onInputChange}
          dir={isArabic ? "rtl" : "ltr"}
          slotProps={{
            paper: {
              sx: {
                background: "#1c1c1c",
                border: "1px solid rgba(255, 174, 0, 0.2)",
                borderRadius: "16px",
                color: "#fff",
                "& .MuiAutocomplete-option": {
                  fontFamily: "Poppins, Ozen",
                  fontSize: "0.9rem",
                  "&[aria-selected='true']": {
                    backgroundColor: "rgba(255, 174, 0, 0.15) !important",
                  },
                  "&.Mui-focused": {
                    backgroundColor: "rgba(255, 174, 0, 0.08) !important",
                  },
                },
              },
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              required={required && !value && !inputValue}
              dir={isArabic ? "rtl" : "ltr"}
              placeholder={placeholderText}
              sx={{
                "& .MuiOutlinedInput-root": {
                  color: "#fff",
                  fontFamily: "Poppins, Ozen",
                  borderRadius: "16px",
                  backgroundColor: "rgba(255, 255, 255, 0.02)",
                  backdropFilter: "blur(8px)",
                  "& input": {
                    textAlign: isArabic ? "right" : "left",
                    padding: "16px 14px",
                  },
                  "& fieldset": {
                    borderColor: "rgba(255, 174, 0, 0.12)",
                    borderRadius: "16px",
                    transition: "all 0.3s ease",
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(255, 174, 0, 0.35)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#FFAE00",
                    boxShadow: "0 0 15px rgba(255, 174, 0, 0.15)",
                  },
                },
              }}
            />
          )}
        />
      </Box>
    );
  };

  const renderSelect = ({
    required = false,
    labelAr,
    labelEn,
    placeholderAr,
    placeholderEn,
    options,
    value,
    onChange,
    icon: IconComponent,
  }: {
    required?: boolean;
    labelAr: string;
    labelEn: string;
    placeholderAr?: string;
    placeholderEn?: string;
    options: { key: string; nameAr: string; nameEn: string }[];
    value: string;
    onChange: (val: string) => void;
    icon: any;
  }) => {
    const labelText = isArabic ? labelAr : labelEn;
    const placeholderText = isArabic ? placeholderAr : placeholderEn;

    return (
      <Box sx={{ width: "100%", mb: 3 }}>
        <Typography
          variant="body2"
          sx={{
            color: "#aaa",
            fontWeight: 600,
            mb: 1.2,
            display: "flex",
            alignItems: "center",
            flexDirection: isArabic ? "row-reverse" : "row",
            justifyContent: isArabic ? "flex-end" : "flex-start",
            width: "100%",
            gap: 1,
            fontFamily: "Poppins, Ozen",
            fontSize: "0.9rem",
          }}
        >
          {IconComponent && (
            <IconComponent sx={{ color: "#FFAE00", fontSize: "1.2rem" }} />
          )}
          <span>{labelText}</span>
          {required && <span style={{ color: "#ff4d4d", marginLeft: 2, marginRight: 2 }}>*</span>}
        </Typography>
        <FormControl fullWidth>
          <Select
            value={value}
            onChange={(e) => onChange(e.target.value as string)}
            displayEmpty
            dir={isArabic ? "rtl" : "ltr"}
            renderValue={(selected) => {
              if (!selected) {
                return <span style={{ color: "rgba(255, 255, 255, 0.3)" }}>{placeholderText}</span>;
              }
              const opt = options.find((o) => o.key === selected);
              return opt ? (isArabic ? opt.nameAr : opt.nameEn) : selected;
            }}
            sx={{
              color: "#fff",
              fontFamily: "Poppins, Ozen",
              borderRadius: "16px",
              backgroundColor: "rgba(255, 255, 255, 0.02)",
              backdropFilter: "blur(8px)",
              "& .MuiSelect-select": {
                textAlign: isArabic ? "right" : "left",
                padding: "16px 14px",
                display: "flex",
                alignItems: "center",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(255, 174, 0, 0.12)",
                borderRadius: "16px",
                transition: "all 0.3s ease",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(255, 174, 0, 0.35)",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#FFAE00",
                boxShadow: "0 0 15px rgba(255, 174, 0, 0.15)",
              },
              "& .MuiSvgIcon-root": {
                color: "#FFAE00",
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  background: "#1c1c1c",
                  border: "1px solid rgba(255, 174, 0, 0.2)",
                  borderRadius: "16px",
                  color: "#fff",
                  "& .MuiMenuItem-root": {
                    fontFamily: "Poppins, Ozen",
                    fontSize: "0.9rem",
                    py: 1.2,
                    "&:hover": {
                      backgroundColor: "rgba(255, 174, 0, 0.08)",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "rgba(255, 174, 0, 0.15) !important",
                    },
                  },
                },
              },
            }}
          >
            <MenuItem value="" disabled>
              {placeholderText}
            </MenuItem>
            {options.map((opt) => (
              <MenuItem key={opt.key} value={opt.key}>
                {isArabic ? opt.nameAr : opt.nameEn}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 1200,
        mx: "auto",
        mt: 8,
        mb: 4,
        px: { xs: 2, md: 4 },
        position: "relative",
        zIndex: 10,
      }}
    >
      <Paper
        elevation={10}
        dir={isArabic ? "rtl" : "ltr"}
        sx={{
          p: { xs: 3, md: 6 },
          borderRadius: "32px",
          background: "linear-gradient(145deg, #161616, #0b0b0b)",
          border: "1px solid rgba(255, 174, 0, 0.1)",
          boxShadow: "0 20px 50px rgba(0, 0, 0, 0.5), 0 0 2px rgba(255, 174, 0, 0.2)",
          direction: isArabic ? "rtl" : "ltr",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative Small Car Watermarks on Both Sides */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: "none",
            zIndex: 0,
            opacity: 0.05,
            overflow: "hidden",
          }}
        >
          {/* Left Column of Small Watermarks */}
          {[
            { top: "4%", left: "15px", rotate: -12, scale: 0.9 },
            { top: "18%", left: "25px", rotate: 8, scale: 1.1 },
            { top: "32%", left: "10px", rotate: -5, scale: 0.85 },
            { top: "46%", left: "30px", rotate: 15, scale: 1.0 },
            { top: "60%", left: "12px", rotate: -10, scale: 0.95 },
            { top: "74%", left: "22px", rotate: 6, scale: 1.05 },
            { top: "88%", left: "8px", rotate: -14, scale: 0.9 },
          ].map((item, idx) => (
            <Box
              key={`left-car-${idx}`}
              component="svg"
              viewBox="0 0 640 280"
              sx={{
                position: "absolute",
                top: item.top,
                left: item.left,
                width: { xs: "70px", md: "110px" },
                transform: `rotate(${item.rotate}deg) scale(${item.scale})`,
                fill: "#FFAE00",
              }}
            >
              <path d="M120,200 Q130,120 200,100 L260,90 Q310,60 380,55 L440,58 Q500,62 530,90 L560,110 Q590,130 600,160 L610,180 Q615,195 610,200 L590,210 Q580,215 570,210 L560,195 Q555,170 535,165 Q515,160 500,170 Q485,185 490,210 L480,215 L220,215 L210,210 Q215,185 200,170 Q185,160 165,165 Q145,175 140,195 L130,210 Q120,215 110,210 Z" />
            </Box>
          ))}

          {/* Right Column of Small Watermarks */}
          {[
            { top: "2%", right: "20px", rotate: 10, scale: 1.05 },
            { top: "15%", right: "10px", rotate: -8, scale: 0.9 },
            { top: "29%", right: "28px", rotate: 12, scale: 1.15 },
            { top: "43%", right: "12px", rotate: -15, scale: 0.85 },
            { top: "57%", right: "24px", rotate: 7, scale: 1.0 },
            { top: "71%", right: "14px", rotate: -6, scale: 0.95 },
            { top: "85%", right: "26px", rotate: 14, scale: 1.1 },
          ].map((item, idx) => (
            <Box
              key={`right-car-${idx}`}
              component="svg"
              viewBox="0 0 640 280"
              sx={{
                position: "absolute",
                top: item.top,
                right: item.right,
                width: { xs: "70px", md: "110px" },
                transform: `rotate(${item.rotate}deg) scale(${item.scale}) scaleX(-1)`,
                fill: "#FFAE00",
              }}
            >
              <path d="M100,200 Q110,140 170,110 L240,95 Q300,65 370,60 L430,62 Q490,68 530,95 L555,115 Q580,135 595,165 L605,185 Q608,198 600,205 L585,212 Q575,216 565,212 L555,198 Q550,175 530,168 Q510,162 495,172 Q480,188 485,212 L475,216 L215,216 L205,212 Q210,188 195,172 Q180,162 160,168 Q140,178 135,198 L125,212 Q115,216 105,212 Z" />
            </Box>
          ))}
        </Box>
        <Typography
          variant="h3"
          fontWeight={900}
          textAlign="center"
          gutterBottom
          sx={{
            fontFamily: "Poppins",
            color: "#FFAE00",
            textTransform: "uppercase",
            fontSize: { xs: "2rem", md: "3rem" },
          }}
        >
          {t("booking.title")}
        </Typography>
        <Typography
          variant="body1"
          textAlign="center"
          sx={{ color: "#aaa", mb: 6, fontFamily: "Poppins", fontSize: { xs: "0.95rem", md: "1.1rem" } }}
        >
          {t("booking.subtitle")}
        </Typography>

        {/* 1. Service Selector Tabs (controls first car's service) */}
        {renderStepHeader("01", "اختر نوع الخدمة المطلوبة", "Select the Service Type")}
        <Box sx={{ mb: 6 }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              justifyContent: "center",
            }}
          >
            {servicesList.map((service) => {
              const isActive = cars[0]?.serviceId === service.id;
              const ServiceIcon = service.icon;
              return (
                <Button
                  key={service.id}
                  onClick={() => handleCarServiceChange(0, service.id)}
                  variant="outlined"
                  startIcon={ServiceIcon ? <ServiceIcon sx={{ [isArabic ? "marginLeft" : "marginRight"]: "8px" }} /> : null}
                  sx={{
                    px: 3,
                    py: 1.8,
                    borderRadius: "16px",
                    fontFamily: "Poppins, Ozen",
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    borderColor: isActive ? "#FFAE00" : "rgba(255, 255, 255, 0.1)",
                    backgroundColor: isActive ? "rgba(255, 174, 0, 0.1)" : "transparent",
                    color: isActive ? "#FFAE00" : "#fff",
                    boxShadow: isActive ? "0 0 20px rgba(255, 174, 0, 0.2)" : "none",
                    transition: "all 0.3s ease",
                    "& .MuiButton-startIcon": {
                      margin: 0,
                      [isArabic ? "marginLeft" : "marginRight"]: "8px",
                    },
                    "&:hover": {
                      borderColor: "#FFAE00",
                      backgroundColor: "rgba(255, 174, 0, 0.05)",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  {isArabic ? service.titleAr : service.titleEn}
                </Button>
              );
            })}
          </Box>
        </Box>

        {/* 2. Car Wash Menu Content (if first car has Washing & Cleaning selected) */}
        <AnimatePresence mode="wait">
          {cars[0]?.serviceId === 7 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
            >
              <Paper
                sx={{
                  p: { xs: 2, md: 4 },
                  mb: 6,
                  borderRadius: "24px",
                  backgroundColor: "rgba(255, 255, 255, 0.02)",
                  border: "1px solid rgba(255, 255, 255, 0.05)",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    p: 0.75,
                    borderRadius: "18px",
                    backgroundColor: "rgba(255, 255, 255, 0.02)",
                    border: "1px solid rgba(255, 255, 255, 0.05)",
                    mb: 4,
                    justifyContent: "space-between",
                    alignItems: "stretch",
                  }}
                >
                  {[
                    { id: 0, labelAr: "غسيل وتنظيف", labelEn: "Wash & Clean" },
                    { id: 1, labelAr: "كار كير", labelEn: "Car Care" },
                    { id: 2, labelAr: "تلميع وحماية", labelEn: "Polish & Protect" },
                  ].map((cat) => {
                    const isActive = cars[0]?.carWashTab === cat.id;
                    return (
                      <Button
                        key={cat.id}
                        onClick={() => handleCarWashTabChange(0, cat.id)}
                        sx={{
                          flex: 1,
                          py: 1.2,
                          px: { xs: 0.5, sm: 1.5 },
                          borderRadius: "14px",
                          fontSize: { xs: "0.72rem", sm: "0.85rem", md: "0.95rem" },
                          fontWeight: 700,
                          lineHeight: 1.2,
                          fontFamily: "Poppins, Ozen",
                          color: isActive ? "#FFAE00" : "#aaa",
                          backgroundColor: isActive ? "rgba(255, 174, 0, 0.08)" : "transparent",
                          border: isActive ? "1px solid rgba(255, 174, 0, 0.25)" : "1px solid transparent",
                          boxShadow: isActive ? "0 4px 15px rgba(255, 174, 0, 0.08)" : "none",
                          transition: "all 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
                          textTransform: "none",
                          whiteSpace: "normal",
                          wordBreak: "keep-all",
                          textAlign: "center",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          "&:hover": {
                            backgroundColor: isActive ? "rgba(255, 174, 0, 0.12)" : "rgba(255, 255, 255, 0.02)",
                            color: isActive ? "#FFAE00" : "#fff",
                          },
                        }}
                      >
                        {isArabic ? cat.labelAr : cat.labelEn}
                      </Button>
                    );
                  })}
                </Box>

                {/* Sub Menu Package Grid */}
                <Grid container spacing={{ xs: 1.5, md: 3 }} justifyContent="center" dir={isArabic ? "rtl" : "ltr"} sx={{ direction: isArabic ? "rtl" : "ltr" }}>
                  {getCarWashCategoriesForCar(cars[0]?.carWashTab ?? 0).map((pkg: any) => {
                    const isSelected = cars[0]?.selectedPackage?.id === pkg.id;
                    return (
                      <Grid size={{ xs: 12, md: 4 }} key={pkg.id}>
                        <motion.div
                          whileHover={{ scale: 1.02 }}
                          transition={{ duration: 0.2 }}
                        >
                          <Paper
                            onClick={() => handleCarPackageSelect(0, pkg)}
                            sx={{
                              p: { xs: 2, md: 3 },
                              height: "100%",
                              borderRadius: "24px",
                              cursor: "pointer",
                              background: isSelected
                                ? "linear-gradient(135deg, rgba(255, 174, 0, 0.12), rgba(0, 0, 0, 0.85))"
                                : "rgba(255, 255, 255, 0.02)",
                              border: isSelected
                                ? "2px solid #FFAE00"
                                : "1px solid rgba(255, 255, 255, 0.06)",
                              boxShadow: isSelected
                                ? "0 10px 30px rgba(255, 174, 0, 0.15)"
                                : "none",
                              backdropFilter: "blur(10px)",
                              transition: "all 0.3s ease",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "space-between",
                            }}
                          >
                            <Box>
                               {pkg.image && (
                                 <Box
                                   component="img"
                                   src={pkg.image}
                                   alt={isArabic ? pkg.nameAr : pkg.nameEn}
                                   sx={{
                                     width: "100%",
                                     height: { xs: 90, md: 140 },
                                     objectFit: "cover",
                                     borderRadius: "14px",
                                     mb: { xs: 1, md: 2 },
                                     border: isSelected
                                       ? "1px solid #FFAE00"
                                       : "1px solid rgba(255, 255, 255, 0.1)",
                                     transition: "all 0.3s ease",
                                   }}
                                 />
                               )}
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="flex-start"
                                mb={1}
                              >
                                <Typography
                                  fontWeight={800}
                                  sx={{
                                    color: isSelected ? "#FFAE00" : "#fff",
                                    fontFamily: "Poppins, Ozen",
                                    fontSize: { xs: "0.95rem", md: "1.15rem" }
                                  }}
                                >
                                  {isArabic ? pkg.nameAr : pkg.nameEn}
                                </Typography>
                                {isSelected && (
                                  <LocalCarWashIcon sx={{ color: "#FFAE00", fontSize: { xs: "1.1rem", md: "1.5rem" } }} />
                                )}
                              </Box>
                              
                              <Box sx={{ minHeight: { xs: "auto", md: 120 }, mb: 2 }}>
                                { (isArabic ? pkg.detailsAr : pkg.detailsEn).map((detail: string, idx: number) => (
                                  <Typography
                                    key={idx}
                                    variant="body2"
                                    sx={{
                                      color: "#bbb",
                                      mb: { xs: 0.4, md: 0.8 },
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      fontSize: { xs: "0.72rem", md: "0.85rem" },
                                      fontFamily: "Poppins, Ozen",
                                      lineHeight: 1.3,
                                      textAlign: isArabic ? "right" : "left"
                                    }}
                                  >
                                    <AutoAwesomeIcon sx={{ color: "#FFAE00", fontSize: { xs: "0.72rem", md: "0.85rem" }, flexShrink: 0 }} />
                                    <span>{detail}</span>
                                  </Typography>
                                )) }
                              </Box>
                            </Box>

                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              pt={1.5}
                              sx={{ borderTop: "1px solid rgba(255, 255, 255, 0.08)" }}
                            >
                              <Typography variant="body2" sx={{ color: "#888", fontFamily: "Poppins, Ozen", fontSize: { xs: "0.75rem", md: "0.85rem" } }}>
                                {isArabic ? "السعر" : "Price"}
                              </Typography>
                              <Typography
                                fontWeight={800}
                                sx={{ color: "#FFAE00", fontFamily: "Poppins", fontSize: { xs: "1.1rem", md: "1.35rem" } }}
                              >
                                {pkg.price} {t("booking.currency")}
                              </Typography>
                            </Box>
                          </Paper>
                        </motion.div>
                      </Grid>
                    );
                  })}
                </Grid>
              </Paper>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Form Inputs divided into Steps */}
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 4 }}>
          {error && (
            <Alert
              severity="warning"
              sx={{
                mb: 4,
                borderRadius: "16px",
                backgroundColor: "rgba(255, 174, 0, 0.05)",
                color: "#ffc107",
                border: "1px solid rgba(255, 174, 0, 0.2)",
                "& .MuiAlert-icon": { color: "#FFAE00" },
                flexDirection: isArabic ? "row-reverse" : "row",
              }}
              action={
                <Button
                  color="warning"
                  size="small"
                  onClick={handleWhatsAppRedirect}
                  startIcon={<WhatsAppIcon />}
                  sx={{
                    fontFamily: "Poppins, Ozen",
                    fontWeight: 700,
                    mx: 2,
                    textTransform: "none",
                  }}
                >
                  {t("booking.whatsappButton")}
                </Button>
              }
            >
              <Typography variant="body2" sx={{ fontFamily: "Poppins, Ozen", textAlign: isArabic ? "right" : "left" }}>
                {error}
              </Typography>
            </Alert>
          )}

          {/* Step 2: Date & Place */}
          {renderStepHeader("02", "موعد وموقع تقديم الخدمة", "Service Schedule & Location")}
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              {renderTextField({
                required: true,
                labelAr: "تاريخ ووقت الحجز",
                labelEn: "Date & Time",
                value: dateTime ? (() => {
                  const parts = dateTime.split("T");
                  const datePart = parts[0];
                  const timePart = parts[1];
                  if (!timePart) return dateTime;
                  const [hours, minutes] = timePart.split(":");
                  const hrNum = Number(hours);
                  const ampm = hrNum >= 12 ? (isArabic ? "م" : "PM") : (isArabic ? "ص" : "AM");
                  const hr12 = hrNum % 12 || 12;
                  return `${datePart} ${String(hr12).padStart(2, '0')}:${minutes} ${ampm}`;
                })() : "",
                readOnly: true,
                onClick: () => setCustomPickerOpen(true),
                icon: CalendarMonthIcon,
                type: "text",
              })}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              {renderTextField({
                required: true,
                labelAr: "العنوان / الموقع بالتفصيل",
                labelEn: "Detailed Address / Location",
                placeholderAr: "أدخل العنوان بالكامل أو موقعك",
                placeholderEn: "Enter your full address or Google Maps location link",
                value: location,
                onChange: setLocation,
                icon: LocationOnIcon,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setMapOpen(true)}
                      sx={{ color: "#FFAE00" }}
                      edge="end"
                    >
                      <MapIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              })}
            </Grid>
          </Grid>

          {/* Step 3: Client Details */}
          {renderStepHeader("03", "بيانات العميل وتفاصيل السيارة", "Client & Car Information")}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              {renderTextField({
                required: true,
                labelAr: "الاسم بالكامل",
                labelEn: "Full Name",
                placeholderAr: "أدخل الاسم بالكامل",
                placeholderEn: "Enter your full name",
                value: fullName,
                onChange: setFullName,
                icon: PersonIcon,
              })}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              {renderTextField({
                required: true,
                labelAr: "رقم الهاتف",
                labelEn: "Phone Number",
                placeholderAr: "أدخل رقم الهاتف",
                placeholderEn: "Enter your phone number",
                value: phone,
                onChange: setPhone,
                icon: PhoneIcon,
              })}
            </Grid>
          </Grid>

          {/* Dynamic Cars Sub-section */}
          <Box sx={{ mb: 4 }}>
            {cars.map((car, cIdx) => (
              <Box
                key={cIdx}
                sx={{
                  p: { xs: 2.5, md: 4 },
                  mb: 3,
                  borderRadius: "24px",
                  background: "rgba(255, 255, 255, 0.01)",
                  border: "1px solid rgba(255, 174, 0, 0.12)",
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
                }}
              >
                {/* Car Header & Delete Button */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={800}
                    sx={{
                      color: "#FFAE00",
                      fontFamily: "Poppins, Ozen",
                      fontSize: "1.1rem",
                    }}
                  >
                    {isArabic ? `بيانات السيارة #${cIdx + 1}` : `Car Details #${cIdx + 1}`}
                  </Typography>
                  {cars.length > 1 && (
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      onClick={() => handleRemoveCar(cIdx)}
                      sx={{
                        borderRadius: "10px",
                        fontFamily: "Poppins, Ozen",
                        fontWeight: 700,
                        borderColor: "rgba(244, 67, 54, 0.4)",
                        "&:hover": {
                          borderColor: "rgb(244, 67, 54)",
                          backgroundColor: "rgba(244, 67, 54, 0.05)",
                        },
                      }}
                    >
                      {isArabic ? "حذف السيارة" : "Remove Car"}
                    </Button>
                  )}
                </Box>

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                    {renderAutocomplete({
                      required: true,
                      labelAr: "ماركة السيارة",
                      labelEn: "Car Brand / Make",
                      placeholderAr: "اختر الماركة أو اكتبها",
                      placeholderEn: "Select make or type...",
                      options: brandsList,
                      getOptionLabel: (option) => (isArabic ? option.name_ar : option.name_en),
                      value: car.selectedBrand,
                      onChange: (event, newValue) => {
                        updateCar(cIdx, {
                          selectedBrand: newValue,
                          selectedModel: null,
                          modelInput: "",
                        });
                      },
                      inputValue: car.brandInput,
                      onInputChange: (event, newInputValue) => {
                        updateCar(cIdx, { brandInput: newInputValue });
                      },
                      icon: DirectionsCarIcon,
                    })}
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                    {renderAutocomplete({
                      required: true,
                      labelAr: "موديل السيارة",
                      labelEn: "Car Model",
                      placeholderAr: car.selectedBrand ? "اختر الموديل أو اكتبه" : "اختر الماركة أولاً",
                      placeholderEn: car.selectedBrand ? "Select model or type..." : "Select brand first",
                      options: car.selectedBrand ? car.selectedBrand.models : [],
                      getOptionLabel: (option) => (isArabic ? option.name_ar : option.name_en),
                      value: car.selectedModel,
                      onChange: (event, newValue) => {
                        updateCar(cIdx, { selectedModel: newValue });
                      },
                      inputValue: car.modelInput,
                      onInputChange: (event, newInputValue) => {
                        updateCar(cIdx, { modelInput: newInputValue });
                      },
                      icon: DirectionsCarIcon,
                      disabled: !car.selectedBrand && !car.brandInput,
                    })}
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4, md: 4 }}>
                    {renderSelect({
                      required: true,
                      labelAr: "لون السيارة",
                      labelEn: "Car Color",
                      placeholderAr: "اختر اللون",
                      placeholderEn: "Select color...",
                      options: carColors,
                      value: car.color,
                      onChange: (val) => updateCar(cIdx, { color: val }),
                      icon: PaletteIcon,
                    })}
                  </Grid>
                  <Grid size={{ xs: 6, sm: 4, md: 4 }}>
                    {renderTextField({
                      required: false,
                      labelAr: "حروف اللوحة",
                      labelEn: "Plate Letters",
                      placeholderAr: "مثال: أ ب ج",
                      placeholderEn: "e.g. A B C",
                      value: car.plateLetters,
                      onChange: (val) => {
                        // Allow only letters (English & Arabic) and spaces
                        const filtered = val.replace(/[^a-zA-Z\u0600-\u06FF\s]/g, "");
                        updateCar(cIdx, { plateLetters: filtered });
                      },
                    })}
                  </Grid>
                  <Grid size={{ xs: 6, sm: 4, md: 4 }}>
                    {renderTextField({
                      required: false,
                      labelAr: "أرقام اللوحة",
                      labelEn: "Plate Numbers",
                      placeholderAr: "مثال: 1234",
                      placeholderEn: "e.g. 1234",
                      value: car.plateNumbers,
                      onChange: (val) => {
                        // Allow only digits (English 0-9 & Arabic/Indic ٠-٩)
                        const filtered = val.replace(/[^0-9\u0660-\u0669]/g, "");
                        updateCar(cIdx, { plateNumbers: filtered });
                      },
                    })}
                  </Grid>
                </Grid>

                {/* Per-car service selector for car #2+ */}
                {cIdx > 0 && (
                  <Box sx={{ mt: 3, pt: 3, borderTop: "1px dashed rgba(255, 174, 0, 0.15)" }}>
                    {/* Toggle: Same service / Different service */}
                    <Box display="flex" gap={2} mb={2} flexWrap="wrap">
                      <Button
                        size="small"
                        variant={car.useGlobalService ? "contained" : "outlined"}
                        onClick={() => { if (!car.useGlobalService) handleToggleGlobalService(cIdx); }}
                        sx={{
                          borderRadius: "12px",
                          fontFamily: "Poppins, Ozen",
                          fontWeight: 700,
                          fontSize: "0.8rem",
                          px: 2.5,
                          py: 1,
                          ...(car.useGlobalService
                            ? {
                                background: "linear-gradient(135deg, #FFAE00, #FF8F00)",
                                color: "#000",
                                "&:hover": { background: "linear-gradient(135deg, #ffc107, #FFAE00)" },
                              }
                            : {
                                borderColor: "rgba(255, 174, 0, 0.3)",
                                color: "#aaa",
                                "&:hover": { borderColor: "#FFAE00", color: "#FFAE00" },
                              }),
                        }}
                      >
                        {isArabic ? "✅ نفس الخدمة" : "✅ Same Service"}
                      </Button>
                      <Button
                        size="small"
                        variant={!car.useGlobalService ? "contained" : "outlined"}
                        onClick={() => { if (car.useGlobalService) handleToggleGlobalService(cIdx); }}
                        sx={{
                          borderRadius: "12px",
                          fontFamily: "Poppins, Ozen",
                          fontWeight: 700,
                          fontSize: "0.8rem",
                          px: 2.5,
                          py: 1,
                          ...(!car.useGlobalService
                            ? {
                                background: "linear-gradient(135deg, #FFAE00, #FF8F00)",
                                color: "#000",
                                "&:hover": { background: "linear-gradient(135deg, #ffc107, #FFAE00)" },
                              }
                            : {
                                borderColor: "rgba(255, 174, 0, 0.3)",
                                color: "#aaa",
                                "&:hover": { borderColor: "#FFAE00", color: "#FFAE00" },
                              }),
                        }}
                      >
                        {isArabic ? "🔄 خدمة مختلفة" : "🔄 Different Service"}
                      </Button>
                    </Box>

                    {/* Show inline service/package selector if "Different Service" is chosen */}
                    <AnimatePresence mode="wait">
                      {!car.useGlobalService && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          {/* Service type selector (compact) */}
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                            {servicesList.map((service) => {
                              const isActive = car.serviceId === service.id;
                              const ServiceIcon = service.icon;
                              return (
                                <Button
                                  key={service.id}
                                  size="small"
                                  onClick={() => handleCarServiceChange(cIdx, service.id)}
                                  variant="outlined"
                                  startIcon={ServiceIcon ? <ServiceIcon sx={{ fontSize: "0.9rem !important" }} /> : null}
                                  sx={{
                                    px: 1.5,
                                    py: 0.8,
                                    borderRadius: "10px",
                                    fontFamily: "Poppins, Ozen",
                                    fontWeight: 600,
                                    fontSize: "0.75rem",
                                    borderColor: isActive ? "#FFAE00" : "rgba(255, 255, 255, 0.08)",
                                    backgroundColor: isActive ? "rgba(255, 174, 0, 0.1)" : "transparent",
                                    color: isActive ? "#FFAE00" : "#888",
                                    "& .MuiButton-startIcon": { margin: 0, mr: 0.5 },
                                    "&:hover": {
                                      borderColor: "#FFAE00",
                                      backgroundColor: "rgba(255, 174, 0, 0.05)",
                                    },
                                  }}
                                >
                                  {isArabic ? service.titleAr : service.titleEn}
                                </Button>
                              );
                            })}
                          </Box>

                          {/* Package selector if wash service */}
                          {car.serviceId === 7 && (
                            <Box>
                              {/* Wash category tabs */}
                              <Box sx={{ display: "flex", gap: 0.5, mb: 2 }}>
                                {[
                                  { id: 0, labelAr: "غسيل", labelEn: "Wash" },
                                  { id: 1, labelAr: "كار كير", labelEn: "Care" },
                                  { id: 2, labelAr: "تلميع", labelEn: "Polish" },
                                ].map((cat) => {
                                  const isActive = car.carWashTab === cat.id;
                                  return (
                                    <Button
                                      key={cat.id}
                                      size="small"
                                      onClick={() => handleCarWashTabChange(cIdx, cat.id)}
                                      sx={{
                                        flex: 1,
                                        py: 0.6,
                                        borderRadius: "8px",
                                        fontSize: "0.72rem",
                                        fontWeight: 700,
                                        fontFamily: "Poppins, Ozen",
                                        color: isActive ? "#FFAE00" : "#888",
                                        backgroundColor: isActive ? "rgba(255, 174, 0, 0.08)" : "transparent",
                                        border: isActive ? "1px solid rgba(255, 174, 0, 0.2)" : "1px solid transparent",
                                        textTransform: "none",
                                      }}
                                    >
                                      {isArabic ? cat.labelAr : cat.labelEn}
                                    </Button>
                                  );
                                })}
                              </Box>
                              {/* Package list compact */}
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                {getCarWashCategoriesForCar(car.carWashTab).map((pkg: any) => {
                                  const isSelected = car.selectedPackage?.id === pkg.id;
                                  return (
                                    <Paper
                                      key={pkg.id}
                                      onClick={() => handleCarPackageSelect(cIdx, pkg)}
                                      sx={{
                                        p: 1.5,
                                        borderRadius: "12px",
                                        cursor: "pointer",
                                        flex: "1 1 calc(33% - 8px)",
                                        minWidth: 120,
                                        background: isSelected
                                          ? "rgba(255, 174, 0, 0.1)"
                                          : "rgba(255, 255, 255, 0.01)",
                                        border: isSelected
                                          ? "2px solid #FFAE00"
                                          : "1px solid rgba(255, 255, 255, 0.06)",
                                        transition: "all 0.2s ease",
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        fontWeight={700}
                                        sx={{
                                          color: isSelected ? "#FFAE00" : "#ccc",
                                          fontFamily: "Poppins, Ozen",
                                          fontSize: "0.75rem",
                                          mb: 0.5,
                                        }}
                                      >
                                        {isArabic ? pkg.nameAr : pkg.nameEn}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        fontWeight={800}
                                        sx={{ color: "#FFAE00", fontFamily: "Poppins", fontSize: "0.85rem" }}
                                      >
                                        {pkg.price} {t("booking.currency")}
                                      </Typography>
                                    </Paper>
                                  );
                                })}
                              </Box>
                            </Box>
                          )}

                          {/* Show selected service summary chip */}
                          {car.serviceId !== 7 && (
                            <Typography variant="body2" sx={{ color: "#aaa", fontFamily: "Poppins, Ozen", fontStyle: "italic", mt: 1 }}>
                              {isArabic ? "سيتم التواصل معك لتحديد التفاصيل والسعر" : "We'll contact you to confirm details and pricing"}
                            </Typography>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Show effective service summary when using global service */}
                    {car.useGlobalService && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                        <LocalCarWashIcon sx={{ color: "#FFAE00", fontSize: "1rem" }} />
                        <Typography variant="body2" sx={{ color: "#aaa", fontFamily: "Poppins, Ozen", fontSize: "0.85rem" }}>
                          {(() => {
                            const eff = getEffectiveService(car, cars[0]);
                            const svcObj = getServiceObjById(eff.serviceId);
                            const svcName = isArabic ? svcObj.titleAr : svcObj.titleEn;
                            if (eff.serviceId === 7 && eff.selectedPackage) {
                              const pkgName = isArabic ? eff.selectedPackage.nameAr : eff.selectedPackage.nameEn;
                              return `${svcName} - ${pkgName}`;
                            }
                            return svcName;
                          })()}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            ))}

            {/* Add Another Car Button */}
            <Box display="flex" justifyContent="flex-start">
              <Button
                variant="outlined"
                onClick={handleAddCar}
                sx={{
                  borderColor: "rgba(255, 174, 0, 0.4)",
                  color: "#FFAE00",
                  fontWeight: 700,
                  borderRadius: "14px",
                  py: 1.2,
                  px: 3,
                  fontFamily: "Poppins, Ozen",
                  "&:hover": {
                    borderColor: "#FFAE00",
                    backgroundColor: "rgba(255, 174, 0, 0.05)",
                  },
                }}
              >
                {isArabic ? "+ إضافة سيارة أخرى" : "+ Add Another Car"}
              </Button>
            </Box>
          </Box>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              {renderTextField({
                required: false,
                labelAr: "ملاحظات إضافية (اختياري)",
                labelEn: "Additional Notes (Optional)",
                placeholderAr: "أدخل أي ملاحظات إضافية أو تعليمات خاصة لفريق العمل",
                placeholderEn: "Enter any special instructions or notes for the team",
                value: notes,
                onChange: setNotes,
                icon: RateReviewIcon,
                multiline: true,
                rows: 3,
              })}
            </Grid>
          </Grid>

          {/* Promo Code & Price Summary */}
          {hasAnyWashService() && (
            <Box sx={{ mt: 4, mb: 4 }}>
              {renderStepHeader("04", "الرمز الترويجي وملخص الدفع", "Promo Code & Payment Summary")}
              
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid size={{ xs: 12, sm: 8 }}>
                  <TextField
                    fullWidth
                    label={isArabic ? "كوبون الخصم" : "Promo Code"}
                    placeholder={isArabic ? "أدخل الرمز الترويجي" : "Enter coupon code..."}
                    value={promoCodeInput}
                    onChange={(e) => setPromoCodeInput(e.target.value.toUpperCase())}
                    sx={{
                      input: { color: "#fff" },
                      label: { color: "#aaa" },
                      "& .MuiOutlinedInput-root": {
                        color: "#fff",
                        fontFamily: "Poppins, Ozen",
                        borderRadius: "16px",
                        backgroundColor: "rgba(255, 255, 255, 0.02)",
                        backdropFilter: "blur(8px)",
                        "& fieldset": { borderColor: "rgba(255, 174, 0, 0.12)" },
                        "&:hover fieldset": { borderColor: "rgba(255, 174, 0, 0.35)" },
                        "&.Mui-focused fieldset": { borderColor: "#FFAE00" },
                      }
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }} display="flex" alignItems="center">
                  <Button
                    variant="outlined"
                    onClick={handleApplyPromoCode}
                    sx={{
                      borderColor: "#FFAE00",
                      color: "#FFAE00",
                      width: "100%",
                      py: 1.8,
                      borderRadius: "16px",
                      fontWeight: "bold",
                      fontFamily: "Poppins, Ozen",
                      "&:hover": { 
                        borderColor: "#FF8F00", 
                        bgcolor: "rgba(255,174,0,0.08)",
                        transform: "translateY(-2px)"
                      }
                    }}
                  >
                    {isArabic ? "تطبيق الكوبون" : "Apply Code"}
                  </Button>
                </Grid>
                {promoError && (
                  <Grid size={{ xs: 12 }}>
                    <Typography color="error" sx={{ fontFamily: "Poppins, Ozen", fontSize: "0.9rem" }}>
                      {promoError}
                    </Typography>
                  </Grid>
                )}
                {promoSuccess && (
                  <Grid size={{ xs: 12 }}>
                    <Typography sx={{ color: "#4caf50", fontFamily: "Poppins, Ozen", fontSize: "0.9rem" }}>
                      {promoSuccess}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              <Paper
                sx={{
                  p: 3,
                  borderRadius: "20px",
                  background: "rgba(18, 18, 18, 0.4)",
                  border: "1px solid rgba(255,174,0,0.1)",
                }}
              >
                <Typography variant="h6" fontWeight="bold" sx={{ color: "#FFAE00", mb: 2, fontFamily: "Poppins, Ozen" }}>
                  {isArabic ? "تفاصيل التكلفة" : "Cost Breakdown"}
                </Typography>

                {/* Per-car price breakdown */}
                {cars.map((car, idx) => {
                  const firstCar = cars[0];
                  const eff = getEffectiveService(car, firstCar);
                  if (eff.serviceId !== 7 || !eff.selectedPackage) return null;
                  const pkgName = isArabic ? eff.selectedPackage.nameAr : eff.selectedPackage.nameEn;
                  const carPrice = getPackagePrice(eff.selectedPackage.id, eff.selectedPackage.price);
                  const brandName = car.selectedBrand ? (isArabic ? car.selectedBrand.name_ar : car.selectedBrand.name_en) : car.brandInput;
                  return (
                    <Box key={idx} display="flex" justifyContent="space-between" mb={1} sx={{ color: "#ccc" }}>
                      <Typography variant="body2" sx={{ fontFamily: "Poppins, Ozen" }}>
                        {isArabic ? `سيارة #${idx + 1}` : `Car #${idx + 1}`}{brandName ? ` (${brandName})` : ""} — {pkgName}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {carPrice} {t("booking.currency")}
                      </Typography>
                    </Box>
                  );
                })}

                {appliedPromo && (
                  <Box display="flex" justifyContent="space-between" mb={1.5} sx={{ color: "#4caf50" }}>
                    <Typography sx={{ fontFamily: "Poppins, Ozen" }}>
                      {isArabic ? "خصم الكوبون" : "Coupon Discount"} ({appliedPromo.code})
                    </Typography>
                    <Typography sx={{ fontWeight: 600 }}>
                      -{calculateAllCarsDiscount()} {t("booking.currency")}
                    </Typography>
                  </Box>
                )}
                <Divider sx={{ my: 2, bgcolor: "rgba(255,255,255,0.08)" }} />
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ color: "#FFAE00" }}>
                  <Typography sx={{ fontWeight: 800, fontSize: "1.1rem", fontFamily: "Poppins, Ozen" }}>
                    {isArabic ? "السعر النهائي" : "Total Price"}
                    {cars.length > 1 && (
                      <Typography component="span" sx={{ color: "#888", fontSize: "0.8rem", ml: 1, fontWeight: 400 }}>
                        ({cars.length} {isArabic ? "سيارات" : "cars"})
                      </Typography>
                    )}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 900, fontFamily: "Poppins" }}>
                    {calculateAllCarsTotal()} {t("booking.currency")}
                  </Typography>
                </Box>
              </Paper>
            </Box>
          )}

          {/* Action Buttons */}
          <Box
            sx={{
              mt: 6,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 3,
              justifyContent: "center",
            }}
          >
            {/* Submit to DB */}
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{
                px: 6,
                py: 2.2,
                borderRadius: "18px",
                background: "linear-gradient(135deg, #FFAE00 0%, #FF8F00 100%)",
                color: "#000",
                fontWeight: 700,
                fontSize: "1rem",
                fontFamily: "Poppins, Ozen",
                flex: 1,
                boxShadow: "0 8px 24px rgba(255, 174, 0, 0.3)",
                transition: "all 0.3s ease",
                "&:hover": {
                  background: "linear-gradient(135deg, #ffc107 0%, #FFAE00 100%)",
                  boxShadow: "0 12px 35px rgba(255, 174, 0, 0.5)",
                  transform: "translateY(-2px)",
                },
                "&.Mui-disabled": {
                  backgroundColor: "rgba(255, 174, 0, 0.3)",
                  color: "rgba(0,0,0,0.5)",
                },
              }}
            >
              {loading ? (
                <Box display="flex" alignItems="center" gap={2} justifyContent="center">
                  <CircularProgress size={20} sx={{ color: "#000" }} />
                  {t("booking.confirming")}
                </Box>
              ) : (
                t("booking.confirmButton")
              )}
            </Button>

            {/* Direct WhatsApp Checkout */}
            <Button
              variant="outlined"
              onClick={handleWhatsAppRedirect}
              startIcon={<WhatsAppIcon />}
              sx={{
                px: 6,
                py: 2.2,
                borderRadius: "18px",
                borderColor: "#25D366",
                color: "#25D366",
                fontWeight: 700,
                fontSize: "1rem",
                fontFamily: "Poppins, Ozen",
                flex: 1,
                borderWidth: "2px",
                transition: "all 0.3s ease",
                "& .MuiButton-startIcon": {
                  margin: 0,
                  [isArabic ? "marginLeft" : "marginRight"]: "8px",
                },
                "&:hover": {
                  borderWidth: "2px",
                  borderColor: "#128C7E",
                  backgroundColor: "rgba(37, 211, 102, 0.08)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 24px rgba(37, 211, 102, 0.15)",
                },
              }}
            >
              {t("booking.whatsappButton")}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Success Dialog */}
      <Dialog
        open={successDialogOpen}
        onClose={handleCloseSuccessDialog}
        PaperProps={{
          sx: {
            background: "#1c1c1c",
            color: "#fff",
            borderRadius: "24px",
            border: "1px solid rgba(255, 174, 0, 0.2)",
            p: 2,
          },
        }}
      >
        <DialogTitle sx={{ color: "#FFAE00", fontWeight: 800, fontFamily: "Poppins, Ozen", textAlign: "center" }}>
          {isArabic ? "🎉 تم تسجيل الحجز!" : "🎉 Booking Registered!"}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#ccc", fontFamily: "Poppins, Ozen", textAlign: "center", mb: 2 }}>
            {t("booking.success")}
          </Typography>
          <Typography sx={{ color: "#aaa", fontFamily: "Poppins, Ozen", fontSize: "0.85rem", textAlign: "center" }}>
            {isArabic
              ? "ملاحظة: يمكنك أيضاً إرسال تفاصيل الحجز عبر واتساب لتأكيد فوري وسريع مع ممثلي الخدمة."
              : "Note: You can also send details via WhatsApp for instant confirmation with our representatives."}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", gap: 2, pb: 2 }}>
          <Button
            onClick={handleCloseSuccessDialog}
            sx={{
              color: "#aaa",
              fontFamily: "Poppins, Ozen",
              "&:hover": { color: "#fff" },
            }}
          >
            {isArabic ? "إغلاق" : "Close"}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              handleWhatsAppRedirect();
              handleCloseSuccessDialog();
            }}
            startIcon={<WhatsAppIcon />}
            sx={{
              backgroundColor: "#25D366",
              color: "#fff",
              fontWeight: 700,
              fontFamily: "Poppins, Ozen",
              "&:hover": { backgroundColor: "#128C7E" },
            }}
          >
            {isArabic ? "متابعة عبر واتساب" : "Proceed via WhatsApp"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Custom Alert Dialog */}
      <Dialog
        open={customAlert !== null && customAlert.open}
        onClose={() => setCustomAlert(null)}
        PaperProps={{
          sx: {
            background: "#121214",
            border: "1px solid rgba(255, 174, 0, 0.3)",
            borderRadius: "24px",
            padding: "24px",
            color: "#fff",
            maxWidth: "400px",
            width: "90%",
            boxShadow: "0 10px 40px rgba(0, 0, 0, 0.8)",
          }
        }}
      >
        <DialogTitle sx={{ p: 0, mb: 2, display: "flex", alignItems: "center", gap: 1.5, color: "#FFAE00" }}>
          <WarningAmberIcon sx={{ fontSize: "1.75rem" }} />
          <Typography component="span" variant="h6" fontWeight={800} sx={{ fontFamily: "Poppins, Ozen" }}>
            {customAlert?.title}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 0, mb: 3 }}>
          <Typography sx={{ fontFamily: "Poppins, Ozen", color: "#ddd", fontSize: "0.95rem", lineHeight: 1.6, textAlign: isArabic ? "right" : "left" }}>
            {customAlert?.message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 0, justifyContent: "flex-end" }}>
          <Button
            onClick={() => setCustomAlert(null)}
            variant="contained"
            sx={{
              background: "linear-gradient(135deg, #FFAE00, #FF8F00)",
              color: "#000",
              fontWeight: 800,
              borderRadius: "14px",
              px: 4,
              py: 1,
              fontFamily: "Poppins, Ozen",
              "&:hover": {
                background: "linear-gradient(135deg, #FF8F00, #FFAE00)",
              }
            }}
          >
            {isArabic ? "حسناً" : "OK"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Custom Date & Time Picker Dialog */}
      <Dialog
        open={customPickerOpen}
        onClose={() => setCustomPickerOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: "#121214",
            border: "1px solid rgba(255, 174, 0, 0.25)",
            borderRadius: "28px",
            color: "#fff",
            p: { xs: 2, md: 3 },
            boxShadow: "0 15px 50px rgba(0, 0, 0, 0.9)",
          }
        }}
      >
        <DialogTitle sx={{ color: "#FFAE00", fontWeight: 800, fontFamily: "Poppins, Ozen", fontSize: "1.25rem", textAlign: "center", pb: 1 }}>
          {isArabic ? "اختر موعد تقديم الخدمة" : "Select Service Date & Time"}
        </DialogTitle>
        
        <DialogContent sx={{ mt: 1 }}>
          <Grid container spacing={{ xs: 3, md: 4 }}>
            {/* Calendar Column */}
            <Grid size={{ xs: 12, md: 7 }}>
              <Box sx={{ background: "rgba(255, 255, 255, 0.02)", p: 2, borderRadius: "20px", border: "1px solid rgba(255, 255, 255, 0.05)" }}>
                {/* Month navigation */}
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <IconButton 
                    onClick={prevMonth} 
                    disabled={pickerDate.getFullYear() <= new Date().getFullYear() && pickerDate.getMonth() <= new Date().getMonth()}
                    sx={{ color: "#FFAE00", "&.Mui-disabled": { color: "rgba(255,255,255,0.1)" } }}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                  <Typography fontWeight={700} sx={{ fontFamily: "Poppins, Ozen", fontSize: "1.1rem" }}>
                    {isArabic 
                      ? `${monthsAr[pickerDate.getMonth()]} ${pickerDate.getFullYear()}` 
                      : `${monthsEn[pickerDate.getMonth()]} ${pickerDate.getFullYear()}`}
                  </Typography>
                  <IconButton onClick={nextMonth} sx={{ color: "#FFAE00" }}>
                    <ChevronRightIcon />
                  </IconButton>
                </Box>
                
                {/* Days of week header */}
                <Grid container spacing={1} columns={7} mb={1}>
                  {(isArabic ? ["أحد", "اثنين", "ثلاثاء", "أربعاء", "خميس", "جمعة", "سبت"] : ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"]).map((dName, idx) => (
                    <Grid size={{ xs: 1 }} key={idx}>
                      <Typography variant="caption" sx={{ display: "block", textAlign: "center", color: "#888", fontWeight: 600, fontFamily: "Poppins, Ozen" }}>
                        {dName}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
                
                {/* Days grid */}
                <Grid container spacing={1} columns={7}>
                  {getDaysArray().map((day, idx) => {
                    const isSelected = selectedDateStr === day.dateStr && day.dayNum !== null;
                    return (
                      <Grid size={{ xs: 1 }} key={idx}>
                        {day.dayNum !== null ? (
                          <Button
                            onClick={() => handleDaySelect(day.dateStr)}
                            disabled={day.disabled}
                            sx={{
                              width: "100%",
                              aspectRatio: "1/1",
                              minWidth: 0,
                              borderRadius: "12px",
                              fontFamily: "Poppins, Ozen",
                              fontWeight: isSelected ? 800 : 500,
                              fontSize: "0.9rem",
                              backgroundColor: isSelected ? "#FFAE00" : "transparent",
                              color: isSelected ? "#000" : (day.disabled ? "rgba(255,255,255,0.15)" : "#fff"),
                              "&:hover": {
                                backgroundColor: isSelected ? "#FFAE00" : "rgba(255, 174, 0, 0.08)",
                              },
                              "&.Mui-disabled": {
                                color: "rgba(255,255,255,0.15)",
                              }
                            }}
                          >
                            {day.dayNum}
                          </Button>
                        ) : (
                          <Box sx={{ width: "100%", aspectRatio: "1/1" }} />
                        )}
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Grid>
            
            {/* Time Slots Column */}
            <Grid size={{ xs: 12, md: 5 }}>
              <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                <Typography variant="subtitle2" sx={{ color: "#aaa", mb: 1.5, fontWeight: 600, fontFamily: "Poppins, Ozen", display: "flex", alignItems: "center", gap: 1 }}>
                  <AccessTimeIcon sx={{ color: "#FFAE00", fontSize: "1.1rem" }} />
                  {isArabic ? "الوقت المتاح:" : "Available Hours:"}
                </Typography>
                
                <Box 
                  sx={{ 
                    flex: 1, 
                    maxHeight: { xs: "240px", md: "310px" }, 
                    overflowY: "auto", 
                    pr: 0.5,
                    background: "rgba(255, 255, 255, 0.01)",
                    border: "1px solid rgba(255, 255, 255, 0.03)",
                    borderRadius: "20px",
                    p: 2,
                    "&::-webkit-scrollbar": { width: "6px" },
                    "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(255, 174, 0, 0.2)", borderRadius: "4px" }
                  }}
                >
                  <Grid container spacing={1}>
                    {(cars.some(car => getEffectiveService(car, cars[0]).serviceId === 7) ? washSlots : towingSlots).map((timeStr) => {
                      const isSelected = selectedTimeStr === timeStr;
                      const isDisabled = isTimeSlotDisabled(timeStr);
                      
                      return (
                        <Grid size={{ xs: 6 }} key={timeStr}>
                          <Button
                            fullWidth
                            onClick={() => setSelectedTimeStr(timeStr)}
                            disabled={isDisabled}
                            variant={isSelected ? "contained" : "outlined"}
                            sx={{
                              borderRadius: "12px",
                              py: 1,
                              fontFamily: "Poppins, Ozen",
                              fontWeight: isSelected ? 700 : 500,
                              fontSize: "0.85rem",
                              backgroundColor: isSelected ? "#FFAE00" : "transparent",
                              color: isSelected ? "#000" : (isDisabled ? "rgba(255,255,255,0.15)" : "#fff"),
                              borderColor: isSelected ? "transparent" : "rgba(255, 174, 0, 0.2)",
                              "&:hover": {
                                backgroundColor: isSelected ? "#FFAE00" : "rgba(255, 174, 0, 0.08)",
                                borderColor: isSelected ? "transparent" : "rgba(255, 174, 0, 0.4)",
                              },
                              "&.Mui-disabled": {
                                color: "rgba(255,255,255,0.15)",
                                borderColor: "rgba(255,255,255,0.05)",
                              }
                            }}
                          >
                            {(() => {
                              const [hours, minutes] = timeStr.split(":").map(Number);
                              const ampm = hours >= 12 ? (isArabic ? "م" : "PM") : (isArabic ? "ص" : "AM");
                              const hr12 = hours % 12 || 12;
                              return `${String(hr12).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${ampm}`;
                            })()}
                          </Button>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 2, pt: 1, justifyContent: "space-between", borderTop: "1px solid rgba(255, 255, 255, 0.05)", mt: 2 }}>
          <Button
            onClick={() => setCustomPickerOpen(false)}
            sx={{
              color: "#aaa",
              fontFamily: "Poppins, Ozen",
              fontWeight: 600,
              "&:hover": { color: "#fff" }
            }}
          >
            {isArabic ? "إلغاء" : "Cancel"}
          </Button>
          <Button
            onClick={handleConfirmCustomPicker}
            variant="contained"
            sx={{
              background: "linear-gradient(135deg, #FFAE00, #FF8F00)",
              color: "#000",
              fontWeight: 800,
              fontFamily: "Poppins, Ozen",
              borderRadius: "14px",
              px: 4,
              py: 1,
              "&:hover": {
                background: "linear-gradient(135deg, #FF8F00, #FFAE00)",
              }
            }}
          >
            {isArabic ? "تأكيد" : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
      <LocationMapDialog
        open={mapOpen}
        onClose={() => setMapOpen(false)}
        onConfirm={setLocation}
        isArabic={isArabic}
      />
    </Box>
  );
};

const loadLeaflet = () => {
  return new Promise<void>((resolve) => {
    if ((window as any).L) {
      resolve();
      return;
    }
    // Load CSS
    if (!document.getElementById("leaflet-css")) {
      const link = document.createElement("link");
      link.id = "leaflet-css";
      link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.appendChild(link);
    }

    // Load JS
    if (!document.getElementById("leaflet-js")) {
      const script = document.createElement("script");
      script.id = "leaflet-js";
      script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
      script.onload = () => resolve();
      document.body.appendChild(script);
    } else {
      const interval = setInterval(() => {
        if ((window as any).L) {
          clearInterval(interval);
          resolve();
        }
      }, 100);
    }
  });
};

const LocationMapDialog = ({
  open,
  onClose,
  onConfirm,
  isArabic,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (val: string) => void;
  isArabic: boolean;
}) => {
  const mapContainerRef = React.useRef<HTMLDivElement>(null);
  const [map, setMap] = React.useState<any>(null);
  const [marker, setMarker] = React.useState<any>(null);
  const [coords, setCoords] = React.useState<[number, number]>([30.0444, 31.2357]); // Default Cairo
  const [address, setAddress] = React.useState("");
  const [loadingAddress, setLoadingAddress] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [searching, setSearching] = React.useState(false);
  const [dialogEntered, setDialogEntered] = React.useState(false);

  // Initialize Map
  React.useEffect(() => {
    if (!dialogEntered) return;

    let activeMap: any = null;
    let activeMarker: any = null;

    loadLeaflet().then(() => {
      const L = (window as any).L;
      if (!L || !mapContainerRef.current) return;

      // Force container cleanup if any previous map was instance bound
      if (activeMap) {
        activeMap.remove();
      }

      // Initialize map
      activeMap = L.map(mapContainerRef.current).setView(coords, 14);

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
        attribution: '&copy; OpenStreetMap'
      }).addTo(activeMap);

      const customIcon = L.icon({
        iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
        shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [41, 41]
      });

      activeMarker = L.marker(coords, { icon: customIcon, draggable: true }).addTo(activeMap);

      setMap(activeMap);
      setMarker(activeMarker);

      activeMap.on("click", (e: any) => {
        const newCoords: [number, number] = [e.latlng.lat, e.latlng.lng];
        setCoords(newCoords);
      });

      activeMarker.on("dragend", (e: any) => {
        const pos = e.target.getLatLng();
        setCoords([pos.lat, pos.lng]);
      });
    });

    return () => {
      if (activeMap) {
        activeMap.remove();
      }
    };
  }, [dialogEntered]);

  // Sync coords to map & marker, and fetch address
  React.useEffect(() => {
    if (!dialogEntered || !map || !marker) return;

    try {
      marker.setLatLng(coords);
      if (map.getCenter().lat !== coords[0] || map.getCenter().lng !== coords[1]) {
        map.invalidateSize();
        map.panTo(coords);
      }
    } catch (e) {
      console.warn("Leaflet map sync failed:", e);
    }

    setLoadingAddress(true);
    fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${coords[0]}&lon=${coords[1]}&accept-language=${isArabic ? 'ar' : 'en'}`)
      .then((res) => res.json())
      .then((data) => {
        if (data && data.display_name) {
          setAddress(data.display_name);
        } else {
          setAddress(`${coords[0].toFixed(5)}, ${coords[1].toFixed(5)}`);
        }
        setLoadingAddress(false);
      })
      .catch(() => {
        setAddress(`${coords[0].toFixed(5)}, ${coords[1].toFixed(5)}`);
        setLoadingAddress(false);
      });
  }, [coords, map, marker, dialogEntered, isArabic]);

  const handleUseCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setCoords([pos.coords.latitude, pos.coords.longitude]);
        },
        (err) => {
          console.error("Geolocation error:", err);
        }
      );
    }
  };

  const handleSearch = () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=1&accept-language=${isArabic ? 'ar' : 'en'}`)
      .then((res) => res.json())
      .then((data) => {
        if (data && data.length > 0) {
          const first = data[0];
          setCoords([parseFloat(first.lat), parseFloat(first.lon)]);
        }
        setSearching(false);
      })
      .catch(() => {
        setSearching(false);
      });
  };

  const handleConfirm = () => {
    const mapUrl = `https://www.google.com/maps?q=${coords[0]},${coords[1]}`;
    const result = address ? `${address} (${mapUrl})` : mapUrl;
    onConfirm(result);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionProps={{
        onEntered: () => setDialogEntered(true),
        onExited: () => setDialogEntered(false),
      }}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: "#121214",
          border: "1px solid rgba(255, 174, 0, 0.25)",
          borderRadius: "28px",
          color: "#fff",
          p: { xs: 2, md: 3 },
          boxShadow: "0 15px 50px rgba(0, 0, 0, 0.9)",
        }
      }}
    >
      <DialogTitle sx={{ color: "#FFAE00", fontWeight: 800, fontFamily: "Poppins, Ozen", fontSize: "1.25rem", textAlign: "center", pb: 1 }}>
        {isArabic ? "اختر موقعك من الخريطة" : "Select Location on Map"}
      </DialogTitle>

      <DialogContent sx={{ mt: 1, p: 0 }}>
        <Box display="flex" gap={1} mb={2} sx={{ flexDirection: isArabic ? "row-reverse" : "row" }}>
          <TextField
            fullWidth
            size="small"
            placeholder={isArabic ? "ابحث عن شارع، منطقة أو مدينة..." : "Search for a street, area, or city..."}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            sx={{
              input: { color: "#fff" },
              "& .MuiOutlinedInput-root": {
                borderRadius: "14px",
                backgroundColor: "rgba(255, 255, 255, 0.02)",
                "& fieldset": { borderColor: "rgba(255,174,0,0.15)" },
                "&:hover fieldset": { borderColor: "#FFAE00" },
                "&.Mui-focused fieldset": { borderColor: "#FFAE00" },
              },
            }}
          />
          <Button
            onClick={handleSearch}
            variant="contained"
            disabled={searching}
            sx={{
              background: "linear-gradient(135deg, #FFAE00, #FF8F00)",
              color: "#000",
              fontWeight: "bold",
              borderRadius: "14px",
              px: 3,
              "&:hover": {
                background: "linear-gradient(135deg, #FF8F00, #FFAE00)",
              }
            }}
          >
            {searching ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              isArabic ? "بحث" : "Search"
            )}
          </Button>
        </Box>

        <div 
          ref={mapContainerRef} 
          style={{ 
            height: "400px", 
            width: "100%", 
            borderRadius: "20px", 
            border: "1px solid rgba(255, 174, 0, 0.15)",
            zIndex: 1
          }} 
        />

        <Box sx={{ mt: 2, p: 2, background: "rgba(255, 255, 255, 0.02)", borderRadius: "16px", border: "1px solid rgba(255, 255, 255, 0.05)" }}>
          <Typography variant="caption" sx={{ color: "#aaa", display: "block", mb: 0.5, textAlign: isArabic ? "right" : "left" }}>
            {isArabic ? "📍 الموقع المختار حالياً:" : "📍 Currently Selected Location:"}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#fff", textAlign: isArabic ? "right" : "left" }}>
            {loadingAddress ? (
              <CircularProgress size={16} sx={{ color: "#FFAE00", mr: 1, verticalAlign: "middle" }} />
            ) : null}
            <span style={{ verticalAlign: "middle" }}>{address || (isArabic ? "جاري جلب العنوان..." : "Fetching address...")}</span>
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 0, pt: 2, justifyContent: "space-between", borderTop: "1px solid rgba(255, 255, 255, 0.05)", mt: 2, flexDirection: isArabic ? "row-reverse" : "row" }}>
        <Box display="flex" gap={1}>
          <Button
            onClick={onClose}
            sx={{
              color: "#aaa",
              fontFamily: "Poppins, Ozen",
              fontWeight: 600,
              "&:hover": { color: "#fff" }
            }}
          >
            {isArabic ? "إلغاء" : "Cancel"}
          </Button>
          <Button
            onClick={handleUseCurrentLocation}
            sx={{
              borderColor: "#FFAE00",
              color: "#FFAE00",
              border: "1px solid",
              fontFamily: "Poppins, Ozen",
              fontWeight: 600,
              borderRadius: "14px",
              px: 2,
              "&:hover": {
                background: "rgba(255, 174, 0, 0.08)",
              }
            }}
          >
            {isArabic ? "تحديد موقعي الحالي" : "Use Current Location"}
          </Button>
        </Box>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={loadingAddress || !address}
          sx={{
            background: "linear-gradient(135deg, #FFAE00, #FF8F00)",
            color: "#000",
            fontWeight: 800,
            fontFamily: "Poppins, Ozen",
            borderRadius: "14px",
            px: 4,
            py: 1,
            "&:hover": {
              background: "linear-gradient(135deg, #FF8F00, #FFAE00)",
            }
          }}
        >
          {isArabic ? "تأكيد الموقع" : "Confirm Location"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BookingForm;
